import { sampleAnswers, fillSamples, removeSamples } from "./lib/demo-answers";
import { sectionBackDestination } from "./lib/section-navigation";
import { savedProfileBiography } from "./lib/profile-entry";
import { passportLayout } from "./lib/passport-layout";
import { AtlasHelp } from "./components/AtlasHelp";
import { ParticipantFooter } from "./components/ParticipantFooter";
import { ParticipantWorkspaceIntroduction } from "./components/ParticipantWorkspaceIntroduction";
import { ParticipantPreparation, type PreparationArea } from "./components/ParticipantPreparation";
import { ProfessionalProfileCenter } from "./components/ProfessionalProfileCenter";
import { familyInformationReady, preparationStatus, preparationTaskKeys } from "./lib/preparation-status.js";
import { PassportCopyUpload, PassportPdfDropZone } from "./components/PassportCopyUpload";
import { singlePassportPdf } from "./lib/passport-file";
import { PassportUploadReview } from "./components/PassportUploadReview";
import { passportContinueTarget } from "./lib/passport-availability";

import {
  ArrowLeft,
  RotateCw,
  ChevronRight,
  Camera,
  Check,
  ChevronDown,
  CircleHelp,
  House,
  FileCheck2,
  FileText,
  Globe2,
  Link2,
  LockKeyhole,
  Plane,
  Plus,
  Search,
  Sparkles,
  Upload,
  X,
} from "lucide-react";
import { ChangeEvent, createContext, useContext, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { addressDraft, addressTemplate, applicability, answerError, documentCompletion, editablePassportField, mergeSuppliedAnswers, entries, isRequired, record, scalar, type AnswerValue, type Answers, type Entry } from "./lib/form-model";
import { PassportScanHelp } from "./components/PassportScanHelp";
import { PassportAvailabilityForm, emptyPassportAvailability, type PassportAvailability } from "./components/PassportAvailability";
import { passportIssue, passportNotStartedLabel } from "./lib/passport-issue";
import { ExpandingTextarea } from "./components/ExpandingTextarea";
import { DemoRouteControls } from "./components/DemoRouteControls";
import { DEMO_PASSPORT_SWITCHER, experienceCatalog, demoInputsFromSearch, resolveExperience, suppliedExperienceAnswers, type Experience } from "./lib/experience-model";
import documentCopy from "./data/document-copy.json";
import { documentItems, personalizeDocuments, documentCenterRequests } from "./lib/document-center";
import invitationFlow from "./data/invitation-flow.json";
import {
  localized,
  participantContract,
  section,
  type ContractConcept,
  type ContractEntryField,
  type DocumentRequest,
  type ProductLocale,
} from "./lib/participant-contract";
import { VerifiedContact } from "./components/VerifiedContact";
import { PhoneInput } from "./components/PhoneInput";
import { registrationPhoneValid } from "./lib/phone-entry";
import { pageTitle } from "./lib/page-title";
import { guidedProgress } from "./lib/guided-progress";
import { CountryFlag } from "./components/CountryFlag";
import { parseSocialAccount, socialAccountFields } from "./lib/social-account";
import { stateCode, usStates } from "./lib/us-states";
import { countryDisplayName } from "./lib/country-display";
import { isoCountryCodes } from "./lib/countries";
import { accountFieldSeeds, loadParticipantAccount, type ParticipantAccount } from "./participantAccount";
import { mvpApi } from "../services/api";
import { answersFromPersonalDetails, personalDetailsPayload } from "./personalDetails";
import { answersFromProductionWork, productionWorkPayloads, socialMediaPayload, socialProfilesFromApi, travelDocumentsFromApi, travelDocumentsPayload } from "./profileSections";
import { formatApiDetail } from "../utils/apiError";
import { answersFromAccompanyingFamily, answersFromImmigrationHistory, answersFromLegalHistory, accompanyingFamilyPayload, immigrationHistoryPayload, immigrationHistoryPayloads, legalHistoryPayload } from "./historySections";
import { answersFromPassportScan, answersFromSavedPassport, passportDetailsPayload, passportStatusFromSaved, passportStatusPayload } from "./passportSection";
import { answersFromProfessionalProfile, answersFromSynthesizedProfile, professionalProfilePayload } from "./professionalProfile";

type Notice = {
  kind: "success" | "info";
  message: string;
};

type YesNo = "yes" | "no";
const FormDraftContext = createContext<{ drafts: Record<string, unknown>; setDrafts: Dispatch<SetStateAction<Record<string, unknown>>> } | null>(null);
function useFormDraft<T,>(key: string, empty: T): [T, Dispatch<SetStateAction<T>>] {
  const context = useContext(FormDraftContext);
  if (!context) throw new Error("Form draft provider is required");
  const current = (context.drafts[key] as T | undefined) ?? empty;
  return [current, (next) => context.setDrafts((all) => ({
    ...all, [key]: typeof next === "function"
      ? (next as (value: T) => T)((all[key] as T | undefined) ?? empty) : next,
  }))];
}
const ExperienceContext = createContext<{experience: Experience; supplied: Answers; account: ParticipantAccount; onFactsChange: (facts: Record<string, unknown>) => void; demoContext: Record<string, unknown>; onDemoChange: (facts: Record<string, unknown>) => void; routingMessage: string | null} | null>(null);
function useExperience() {
  const context = useContext(ExperienceContext);
  if (!context) throw new Error("Resolved participant experience is required");
  return context;
}


type SocialProfile = {
  platform: string;
  platformOther: string;
  handle: string;
  profileUrl: string;
  accountKind: string;
};

type OtherName = {
  givenName: string;
  middleName: string;
  familyName: string;
};

type FamilyMember = {
  givenNames: string;
  surnames: string;
  legacyFullName?: string;
  legacyNationality?: string;
  birthDate: string;
  passportCountry: string;
  relationship: string;
};

type RegistrationContact = { email: string; phone: string; name: string; givenNames: string; familyName: string };

type InvitationStage = "landing" | "verify" | "register" | "phoneVerify";

function maskEmail(email: string) {
  const [local, domain] = email.split("@");
  return `${local.slice(0, 1)}${"•".repeat(Math.max(4, local.length - 1))}@${domain}`;
}

function InvitationOnboarding({ locale, onLocaleChange, onComplete, mode = "invite" }: { locale: ProductLocale; onLocaleChange: (locale: ProductLocale) => void; onComplete: (contact: RegistrationContact) => void; mode?: "invite" | "inactive" | "signin" }) {
  const invite = invitationFlow.sample;
  const [stage, setStage] = useState<InvitationStage>("landing");
  const [returning, setReturning] = useState(mode === "signin");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [accepted, setAccepted] = useState(false);
  const es = locale === "es";
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const phoneDigits = phone.replace(/\D/g, "");
  const phoneValid = registrationPhoneValid(phone);
  const participantFirstName = invite.participant_name.split(" ")[0];

  const complete = () => onComplete({email: email.trim(), phone: phoneDigits ? phone.trim() : "", name: invite.participant_name, givenNames: invite.participant_given_names, familyName: invite.participant_family_name});

  function verify() {
    if (code.length !== 6) return;
    if (returning) complete();
    else setStage("register");
  }

  function completeRegistration() {
    if (!accepted || !phoneValid) return;
    setCode("");
    setStage("phoneVerify");
  }

  return <main className="invitation-shell">
    <header className="invitation-topbar">
      <div className="invitation-brand"><strong>Global Mobility</strong></div>
      <select aria-label={es ? "Idioma" : "Language"} value={locale} onChange={(event) => onLocaleChange(event.target.value as ProductLocale)}><option value="en">EN</option><option value="es">ES</option></select>
    </header>
    {mode === "invite" && <div className="invitation-progress" aria-hidden="true"><span style={{ width: stage === "landing" ? "25%" : stage === "verify" ? "50%" : stage === "register" ? "75%" : "100%" }} /></div>}

    <section className={`invitation-content invitation-content-${stage}`}>
      {mode === "inactive" && <div className="invitation-recovery">
        <h1>{es ? "Este enlace ya no está activo" : "This invitation link no longer works"}</h1>
        <p>{es ? "Si ya completó su registro, inicie sesión para continuar." : "If you have already registered, sign in to continue."}</p>
        <a className="invitation-primary" href="/?signin=1">{es ? "Iniciar sesión" : "Sign in"}</a>
        <p>{es ? "Si aún necesita registrarse, pida un nuevo enlace a su equipo de producción." : "If you still need to register, ask your production team for a new link."}</p>
      </div>}
      {stage === "landing" && mode !== "inactive" && <>
        <h1>{mode === "signin" ? (es ? "Iniciar sesión" : "Sign in") : es ? <>Únase a <span>{invite.production_name}</span></> : <>Join <span>{invite.production_name}</span></>}</h1>
        {mode === "invite" && <p className="invitation-intro"><strong>{invite.inviter_name}</strong>{es ? " le ha invitado a registrarse en Global Mobility." : " has invited you to register with Global Mobility."}</p>}
        <form className="invitation-email-form" onSubmit={(event) => { event.preventDefault(); if (!emailValid) return; setReturning(mode === "signin"); setStage("verify"); }}>
          <label htmlFor="invitation-email">Email</label>
          <input id="invitation-email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} value={email} onChange={(event) => setEmail(event.target.value)} placeholder={es ? "correo@ejemplo.com" : "name@example.com"} required />
          <p>{es ? "Enviaremos un código de verificación de seis dígitos desde noreply@globalmobility.law." : "We’ll send a six-digit verification code from noreply@globalmobility.law."}</p>
          <button className="invitation-primary" type="submit" disabled={!emailValid}>{es ? "Continuar" : "Continue"}</button>
        </form>
        {mode === "invite" && <a className="invitation-link invitation-signin" href="/?signin=1"><span>{es ? "¿Ya está registrado?" : "Already registered?"}</span><strong>{es ? "Iniciar sesión" : "Sign in"}</strong></a>}
      </>}

      {stage === "verify" && <>
        <button className="invitation-back" onClick={() => { setStage("landing"); setCode(""); }}>← {es ? "Atrás" : "Back"}</button>
        <h1>{es ? "Verifique su correo" : "Verify your email"}</h1>
        <p className="invitation-intro">{es ? <>Introduzca el código de seis dígitos enviado a <strong>{maskEmail(email.trim())}</strong>.</> : <>Enter the six-digit code sent to <strong>{maskEmail(email.trim())}</strong>.</>}</p>
        <label className="code-field"><span className="sr-only">{es ? "Código de verificación" : "Verification code"}</span><input autoFocus value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" /></label>
        <p className="code-help">{es ? "Si el código caduca o no llega, solicite otro." : "If the code expires or doesn’t arrive, request another."}</p>
        <button className="invitation-primary" disabled={code.length !== 6} onClick={verify}>{es ? "Verificar" : "Verify"}</button>
        <button className="invitation-link">{es ? "Enviar otro código" : "Send another code"}</button>
      </>}

      {stage === "register" && <>
        <button className="invitation-back" onClick={() => setStage("verify")}>← {es ? "Atrás" : "Back"}</button>
        <h1>{es ? `Termine su registro, ${participantFirstName}` : `Finish registering, ${participantFirstName}`}</h1>
        <p className="invitation-intro">{invite.production_name}</p>
        <div className="registration-fields">
          <label className="wide"><span>{es ? "Nombre" : "Name"}</span><input value={invite.participant_name} readOnly /></label>
          <label className="wide"><span className="registration-field-heading"><span>Email</span><strong>{es ? "Verificado" : "Verified"}</strong></span><input value={email.trim()} readOnly /></label>
          <div className="wide registration-phone-field"><label htmlFor="registration-phone">{es ? "Teléfono" : "Phone"}</label>
            <PhoneInput id="registration-phone" value={phone} locale={locale} required onChange={setPhone} />
          </div>
          <label className="wide consent"><input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} /><span>{es ? "Acepto los términos y el aviso de privacidad." : "I accept the terms and privacy notice."}</span></label>
        </div>
        <button className="invitation-primary" disabled={!accepted || !phoneValid} onClick={completeRegistration}>{es ? "Completar registro" : "Complete registration"}</button>
      </>}

      {stage === "phoneVerify" && <>
        <button className="invitation-back" onClick={() => { setStage("register"); setCode(""); }}>← {es ? "Atrás" : "Back"}</button>
        <h1>{es ? "Verifique su teléfono" : "Verify your phone"}</h1>
        <p className="invitation-intro">{es ? <>Introduzca el código de seis dígitos enviado al <strong>{phone}</strong>.</> : <>Enter the six-digit code sent to <strong>{phone}</strong>.</>}</p>
        <label className="code-field"><span className="sr-only">{es ? "Código de verificación" : "Verification code"}</span><input autoFocus value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" /></label>
        <button className="invitation-primary" disabled={code.length !== 6} onClick={complete}>{es ? "Verificar teléfono" : "Verify phone"}</button>
        <button className="invitation-link">{es ? "Enviar otro código" : "Send another code"}</button>
      </>}
    </section>
    <ParticipantFooter locale={locale} />
  </main>;
}

function AddressQuestion({ id, value, onChange, locale, label, error, required }: { id: string; value: AnswerValue | undefined; onChange: (value: AnswerValue) => void; locale: ProductLocale; label: string; error?: string; required: boolean }) {
  const {supplied} = useExperience();
  // Editable convenience default only, never evidence of residence or physical location.
  const initialCountry = useRef(String(supplied["pf.passport.issuing_country"] ?? ""));
  useEffect(() => {
    if ((value === undefined || value === "") && initialCountry.current) onChange({mode: "structured", country: initialCountry.current});
  }, [value, onChange]);
  const addressAutocomplete: Record<string, string> = { line1: "address-line1", line2: "address-line2", locality: "address-level2", administrative_area: "address-level1", postal_code: "postal-code" };
  const draft = addressDraft(value);
  const template = addressTemplate(draft.country);
  const freeform = draft.mode === "freeform" || !template;
  const update = (key: string, text: string) => onChange({ ...draft, [key]: text });
  const es = locale === "es";
  return <fieldset id={id} tabIndex={-1} className="question-field address-question" aria-describedby={error ? `${id}-error` : undefined}>
    <legend>{label}</legend>
    <label htmlFor={`${id}-country`}>{es ? "País del domicilio" : "Address country"}</label>
    <CountryPicker id={`${id}-country`} label={es ? "País del domicilio" : "Address country"} locale={locale} multiple={false} value={draft.country ? [draft.country] : []} onChange={(codes) => update("country", codes[0] ?? "")} />
    {freeform ? (draft.country || draft.mode === "freeform") && <label className="address-freeform"><span>{es ? "Dirección completa" : "Full address"}</span>
      <ExpandingTextarea autoComplete="street-address" value={draft.freeform ?? ""} aria-required={required} aria-invalid={Boolean(error)} onChange={(event) => update("freeform", event.target.value)} />
    </label> : <div className="address-fields">{template.fields.map((field) => <label key={field.id} className={field.id.startsWith("line") ? "address-wide" : undefined}>
      <span>{field.label}{!field.required ? (es ? " (opcional)" : " (optional)") : ""}</span>
      {draft.country === "US" && field.id === "administrative_area" ? <select autoComplete="address-level1" value={stateCode(draft[field.id] ?? "")} aria-required={required && field.required} aria-invalid={Boolean(error) && !draft[field.id]?.trim()} onChange={event => update(field.id, event.target.value)}>
        <option value="">{es ? "Seleccionar estado" : "Select state"}</option>
        {draft[field.id] && !usStates.some(state => state.code === stateCode(draft[field.id]!)) && <option value={draft[field.id]}>{draft[field.id]}</option>}
        {usStates.map(state => <option key={state.code} value={state.code}>{state.name}</option>)}
      </select> : <input type="text" autoComplete={addressAutocomplete[field.id]} value={draft[field.id] ?? ""} aria-required={required && field.required} aria-invalid={Boolean(error) && field.required && !draft[field.id]?.trim()} onChange={(event) => update(field.id, event.target.value)} />}
    </label>)}</div>}

    {error && <p id={`${id}-error`} className="field-error" role="alert">{error}</p>}
  </fieldset>;
}

function CountryPicker({ id, label, describedBy, locale, multiple, overlay = false, invalid = false, showFlags = true, progressiveMultiple = false, value = [], onChange }: { id: string; label?: string; describedBy?: string; locale: ProductLocale; multiple: boolean; overlay?: boolean; invalid?: boolean; showFlags?: boolean; progressiveMultiple?: boolean; value?: string[]; onChange?: (codes: string[]) => void }) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string[]>(value);
  const valueKey = value.join(",");
  useEffect(() => setSelected(value), [valueKey]);
  const displayNames = useMemo(
    () => new Intl.DisplayNames([locale], { type: "region" }),
    [locale],
  );
  const countries = useMemo(
    () => isoCountryCodes
      .map((code) => ({ code, name: displayNames.of(code) ?? code }))
      .sort((a, b) => a.name.localeCompare(b.name, locale)),
    [displayNames, locale],
  );
  const normalizedQuery = query.toLocaleLowerCase(locale).trim();
  const matches = countries
    .filter((countryOption) => countryOption.name.toLocaleLowerCase(locale).includes(normalizedQuery))
    .sort((a, b) => {
      const aStarts = a.name.toLocaleLowerCase(locale).startsWith(normalizedQuery);
      const bStarts = b.name.toLocaleLowerCase(locale).startsWith(normalizedQuery);
      return Number(bStarts) - Number(aStarts) || a.name.localeCompare(b.name, locale);
    });
  const searchLabel = locale === "es" ? "Buscar países" : "Search countries";
  const selectLabel = progressiveMultiple
    ? (locale === "es" ? "Seleccionar ciudadanía" : "Select citizenship")
    : locale === "es" ? (multiple ? "Seleccionar países" : "Seleccionar país") : (multiple ? "Select countries" : "Select country");
  const selectedNames = selected.map((code) => displayNames.of(code) ?? code);

  function selectCountry(code: string) {
    if (multiple) {
      const next = selected.includes(code)
        ? selected.filter((item) => item !== code)
        : [...selected, code];
      setSelected(next);
      onChange?.(next);
      if (progressiveMultiple && !selected.includes(code)) detailsRef.current?.removeAttribute("open");
    } else {
      setSelected([code]);
      onChange?.([code]);
      detailsRef.current?.removeAttribute("open");
    }
    setQuery("");
  }

  // Single-country fields share the native dropdown across every participant flow.
  // Keep the searchable picker only for selecting multiple countries.
  if (!multiple) {
    const selectedFlag = showFlags && selected[0] ? <CountryFlag code={selected[0]} className="single-country-flag" /> : null;
    return <div className={`single-country-control${selectedFlag ? " has-flag" : ""}`}>
      {selectedFlag}
      <select id={id} aria-label={label} aria-describedby={describedBy} aria-invalid={invalid} value={selected[0] ?? ""}
        onChange={event => { const next = event.target.value ? [event.target.value] : []; setSelected(next); onChange?.(next); }}>
        <option value="">{selectLabel}</option>
        {countries.map(country => <option key={country.code} value={country.code}>{country.name}</option>)}
      </select>
      <ChevronDown size={16} aria-hidden="true" />
    </div>;
  }

  return (
    <div className={`country-control${progressiveMultiple ? " progressive-country-control" : ""}${selected.length ? " has-selection" : ""}`}>
      <details className={`country-picker${overlay ? " country-picker-overlay" : ""}`} ref={detailsRef}>
        <summary id={id} aria-describedby={describedBy} aria-invalid={invalid} aria-label={label ? `${label}: ${selectedNames.join(", ") || selectLabel}` : undefined}>
          <span className={selected.length === 1 && showFlags ? "selected-country-name" : undefined}>
            {selected.length === 1 && showFlags && <CountryFlag code={selected[0]} />}
            {selected.length
            ? multiple
              ? selectedNames.join(", ")
              : displayNames.of(selected[0])
            : selectLabel}
          </span>
          <ChevronDown size={16} />
        </summary>
        <div className="country-menu">
          {overlay && <div className="country-menu-header"><strong>{selectLabel}</strong><button type="button" aria-label={locale === "es" ? "Cerrar" : "Close"} onClick={() => detailsRef.current?.removeAttribute("open")}><X size={20} /></button></div>}
          <label className="sr-only" htmlFor={`${id}-search`}>{searchLabel}</label>
          <div className="country-search">
            <Search size={18} aria-hidden="true" />
            <input
              id={`${id}-search`}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchLabel}
              autoComplete="off"
              autoCapitalize="words"
            />
          </div>
          <div className="country-results" role="listbox" aria-multiselectable={multiple}>
            {matches.slice(0, 40).map((countryOption) => (
              <button
                type="button"
                role="option"
                aria-selected={selected.includes(countryOption.code)}
                key={countryOption.code}
                onClick={() => selectCountry(countryOption.code)}
              >
                <span className="country-result-name">{showFlags && <CountryFlag code={countryOption.code} />}{countryOption.name}</span>
                {selected.includes(countryOption.code) && <strong>✓</strong>}
              </button>
            ))}
          </div>
          {overlay && multiple && selected.length > 0 && <button className="country-done" type="button" onClick={() => detailsRef.current?.removeAttribute("open")}>{locale === "es" ? "Listo" : "Done"}</button>}
        </div>
      </details>
      {selected.length > 0 && multiple && (
        <div className="country-tags">
          {selected.map((code) => (
            <button type="button" key={code} onClick={() => selectCountry(code)} aria-label={`${locale === "es" ? "Eliminar" : "Remove"} ${displayNames.of(code)}`}>
              <span className="selected-country-name">{showFlags && <CountryFlag code={code} />}{displayNames.of(code)}</span><span aria-hidden="true">×</span>
            </button>
          ))}
        </div>
      )}
      {progressiveMultiple && selected.length > 0 && (
        <button className="add-country-button" type="button" onClick={() => detailsRef.current?.setAttribute("open", "")}>
          <Plus size={15} aria-hidden="true" />
          {locale === "es" ? "Añadir otra ciudadanía" : "Add another citizenship"}
        </button>
      )}
    </div>
  );
}

  const conciseLabels: Record<string, { en: string; es: string }> = {
    "pf.identity.given_names": { en: "First name", es: "Nombre" },
    "pf.identity.middle_names": { en: "Middle name (optional)", es: "Segundo nombre (opcional)" },
    "pf.identity.family_primary": { en: "Last name", es: "Primer apellido" },
    "pf.identity.family_second": { en: "Second family name (optional)", es: "Segundo apellido (opcional)" },
    "pf.identity.birth_city": { en: "City of birth", es: "Ciudad de nacimiento" },
    "pf.identity.nationalities": { en: "Nationality", es: "Nacionalidad" },
    "pf.identity.sex_as_documented": { en: "Sex as shown on passport", es: "Sexo que figura en el pasaporte" },
    "pf.passport.issuing_country": { en: "Issuing country", es: "País de expedición" },
    "pf.passport.issue_date": { en: "Issue date", es: "Fecha de expedición" },
    "pf.family.accompanying": { en: "Might any family members travel with you or join you for this project?", es: "¿Es posible que algún familiar viaje con usted o se reúna con usted para este proyecto?" },
    "pf.career.occupation": { en: "Primary occupation", es: "Ocupación principal" },
    "pf.career.year_started": { en: "Year started", es: "Año de inicio" },
    "pf.career.biography": { en: "Profile", es: "Perfil" },
    "pf.career.credits": { en: "Credits", es: "Créditos" },
    "pf.location.country_of_legal_residence": { en: "Country where you legally live", es: "País donde vive legalmente" },
    "pf.location.current_country": { en: "Country you are physically in today", es: "País en el que se encuentra físicamente hoy" },
    "pf.us.alien_number": { en: "USCIS A-Number, if you have one", es: "Número A de USCIS, si tiene uno" },
    "pf.us.ssn": { en: "U.S. Social Security number, if you have one", es: "Número de Seguro Social de EE. UU., si tiene uno" },
  };

function optionalFieldLabel(label: string, optional: boolean, locale: ProductLocale) {
  return optional && !/\((optional|opcional)\)/i.test(label)
    ? `${label}${locale === "es" ? " (opcional)" : " (optional)"}` : label;
}

function ContractQuestion({
  concept,
  locale,
  answers,
  setAnswer,
  focused = false,
  saveSignal = 0,
  invalid = false,
  error,
  hideLabel = false,
  onOpenProfileAssistant,
  socialProfiles,
  onSocialProfilesChange,
}: {
  concept: ContractConcept;
  locale: ProductLocale;
  answers: Record<string, AnswerValue>;
  setAnswer: (id: string, value: AnswerValue) => void;
  focused?: boolean;
  saveSignal?: number;
  invalid?: boolean;
  error?: string;
  hideLabel?: boolean;
  onOpenProfileAssistant?: () => void;
  socialProfiles?: SocialProfile[];
  onSocialProfilesChange?: (profiles: SocialProfile[]) => void;
}) {
  const {experience, supplied, demoContext} = useExperience();
  const allConcepts = experience.concepts;
  const label = conciseLabels[concept.id]?.[locale] ?? localized(concept.label ?? concept.prompt, locale);
  const id = `question-${concept.id}`;
  const required = isRequired(concept, answers, allConcepts);
  const chooseLabel = locale === "es" ? "Elegir" : "Choose";
  const identifierHelp = concept.id === "pf.us.alien_number"
    ? locale === "es"
      ? "USCIS asigna un A-Number a algunas personas que solicitan o reciben determinados beneficios migratorios. Suele empezar por A y aparece en algunos documentos de DHS o USCIS. Déjelo en blanco si nunca le han asignado uno."
      : "USCIS assigns an A-Number to some people who apply for or receive certain immigration benefits. It usually begins with A and appears on some DHS or USCIS documents. Leave this blank if you have never been issued one."
    : concept.id === "pf.us.ssn"
      ? locale === "es"
        ? "Algunas personas que no son ciudadanas estadounidenses tienen un número de Seguro Social por un trabajo autorizado anterior u otro motivo válido. Déjelo en blanco si nunca le han asignado uno."
        : "Some non-U.S. citizens have a Social Security number from earlier authorized work or another qualifying reason. Leave this blank if you have never been issued one."
    : ["pf.legal.arrested", "pf.identity.given_names", "pf.identity.middle_names", "pf.identity.family_primary", "pf.identity.family_second"].includes(concept.id)
      ? localized(concept.help, locale)
      : "";

  const questionLabel = (
    <div className="question-label-row">
      <span>{optionalFieldLabel(label, !required, locale)}</span>
      {identifierHelp && (
        <details className="field-help">
          <summary aria-label={locale === "es" ? `Explicación de ${label}` : `${label} explanation`}><CircleHelp size={17} /></summary>
          <p>{identifierHelp}</p>
        </details>
      )}
    </div>
  );

  if (applicability(concept, answers, allConcepts) !== true) return null;
  const current = answers[concept.id];
  const value = scalar(current);
  const detail = record(current);
  const fieldError = error ?? (invalid ? (locale === "es" ? "Este campo es obligatorio." : "This field is required.") : "");
  const errorMessage = fieldError ? <p id={`${id}-error`} className="field-error" role="alert">{fieldError}</p> : null;
  const inputProps = { "aria-invalid": Boolean(fieldError), "aria-describedby": fieldError ? `${id}-error` : undefined, "aria-required": required };
  if (concept.answer.shape === "postal_address") return <AddressQuestion id={id} value={current} onChange={(next) => setAnswer(concept.id, next)} locale={locale} label={label} error={fieldError} required={required} />;
  if (concept.id === "pf.identity.other_names") {
    const usedRaw = scalar(answers["pf.identity.other_names_used"]).trim().toLowerCase();
    const used = usedRaw === "yes" || usedRaw === "no" ? usedRaw : "";
    return <OtherNamesQuestion locale={locale} saveSignal={saveSignal} names={entries(current).map((item) => ({givenName: item.given_name, middleName: item.middle_name, familyName: item.family_name}))}
      used={used}
      onUsedChange={(value) => setAnswer("pf.identity.other_names_used", value)}
      onNamesChange={(names) => setAnswer(concept.id, names.map((name) => ({ given_name: name.givenName, middle_name: name.middleName, family_name: name.familyName })))} />;
  }
  if (concept.id === "pf.family.members") {
    return <FamilyMemberRepeater locale={locale} travelPlans={scalar(answers["pf.family.travel_plans"])} onTravelPlansChange={value => setAnswer("pf.family.travel_plans", value)} members={entries(current).map((item) => ({givenNames: item.given_names ?? "", surnames: item.surnames ?? "", legacyFullName: item.full_name, legacyNationality: item.nationality, birthDate: item.birth_date, passportCountry: item.passport_country ?? "", relationship: item.relationship}))}
      onMembersChange={(members) => setAnswer(concept.id, members.map((member) => ({given_names: member.givenNames, surnames: member.surnames, birth_date: member.birthDate, passport_country: member.passportCountry, relationship: member.relationship, ...(member.legacyFullName ? {full_name:member.legacyFullName} : {}), ...(member.legacyNationality ? {nationality:member.legacyNationality} : {})})))} />;
  }

  if (Object.hasOwn(supplied, concept.id) && value.trim() !== "" && !editablePassportField(concept.id) && !concept.id.startsWith("pf.location.")) {
    const contextLabel = concept.answer.options?.find((option) => option.value === value)?.label;
    const displayValue = concept.answer.shape === "country_reference" ? countryDisplayName(value, locale) : localized(contextLabel, locale) || value;
    return <div className="supplied-field"><span>{concept.id === "pf.us.mptv_services" ? (locale === "es" ? "Producción" : "Production") : label}</span><strong>{concept.id === "pf.us.mptv_services" ? (value === "yes" ? (locale === "es" ? "Cine o televisión" : "Film or television") : (locale === "es" ? "Artes" : "Arts")) : displayValue}</strong></div>;
  }

  if (concept.answer.shape === "country_reference") {
    const residenceHelp = concept.id === "pf.location.country_of_legal_residence";
    return (
      <div className={`question-field${invalid ? " invalid" : ""}`}>
        <label className={hideLabel ? "sr-only" : undefined} htmlFor={id}>{questionLabel}</label>
        {residenceHelp && <p id={`${id}-help`} className="country-field-guidance">{locale === "es" ? "Seleccione el país donde vive habitualmente como ciudadano o con autorización para residir. Por ejemplo, si tiene un pasaporte de India pero vive en España con una visa de residencia, seleccione España. Unas vacaciones o una visita breve a otro país no cambian su respuesta." : "Choose the country where you normally live as a citizen or with permission to reside. For example, if you hold an Indian passport but live in Spain with a residence visa, choose Spain. A holiday or short visit to another country does not change your answer."}</p>}
        <CountryPicker
          id={id}
          label={optionalFieldLabel(label, !required, locale)}
          locale={locale}
          multiple={concept.answer.cardinality === "many"}
          overlay={focused}
          invalid={Boolean(fieldError)}
          describedBy={[residenceHelp ? `${id}-help` : "", fieldError ? `${id}-error` : ""].filter(Boolean).join(" ") || undefined}
          progressiveMultiple={concept.id === "pf.identity.nationalities" && concept.answer.cardinality === "many"}
          value={scalar(answers[concept.id]).split(",").filter(Boolean)}
          onChange={(codes) => setAnswer(concept.id, concept.answer.cardinality === "many" ? codes.join(",") : codes[0] ?? "")}
        />
        {errorMessage}
      </div>
    );
  }

  if (concept.answer.shape === "boolean" || concept.answer.shape === "boolean_with_explanation" || (concept.answer.shape === "single_choice" && concept.answer.options?.length === 2 && concept.answer.options.every((option) => ["yes", "no"].includes(option.value)))) {
    const selected = value;
    const choose = (next: string) => setAnswer(concept.id, concept.answer.shape === "boolean_with_explanation"
      ? { value: next, explanation: next === "yes" ? (detail.explanation ?? "") : "" }
      : next);
    return (
      <fieldset id={id} tabIndex={-1} {...inputProps} className={`question-field${invalid ? " invalid" : ""}`}>
        <legend>{questionLabel}</legend>
        <div className="choice-row">
          <label><input type="radio" name={concept.answer_key} value="yes" checked={selected === "yes"} onChange={() => choose("yes")} />{locale === "es" ? "Sí" : "Yes"}</label>
          <label><input type="radio" name={concept.answer_key} value="no" checked={selected === "no"} onChange={() => choose("no")} />No</label>
        </div>
        {concept.answer.shape === "boolean_with_explanation" && selected === "yes" && (
          <label className="explanation-field">
            <span>{locale === "es" ? "Explique brevemente" : "Brief details"}</span>
            <ExpandingTextarea value={detail.explanation ?? ""} onChange={(event) => setAnswer(concept.id, { ...detail, value: selected, explanation: event.target.value })} />
          </label>
        )}
        {errorMessage}
      </fieldset>
    );
  }

  return (
    <div data-field-id={concept.id} className={`question-field${concept.answer.shape === "calendar_year" ? " short-field" : ""}${concept.id === "pf.career.credits" ? " credit-question" : ""}${concept.id === "pf.social.accounts" ? " social-question" : ""}${concept.id === "pf.passport.other_details" ? " travel-document-question" : ""}${invalid ? " invalid" : ""}`}>
      <label htmlFor={id}>{questionLabel}</label>
      {concept.answer.shape === "language_reference" ? (
        <select id={id} value={value} {...inputProps} onChange={(event) => setAnswer(concept.id, event.target.value)}><option value="">Choose</option>{participantContract.locales.product_locales.map((code) => <option key={code} value={code}>{code === "en" ? "English" : "Español"}</option>)}</select>
      ) : concept.answer.shape === "single_choice" && concept.answer.options?.length ? (
        <select id={id} name={concept.id} value={value} {...inputProps} onChange={(event) => setAnswer(concept.id, event.target.value)}>
          <option value="" disabled={concept.id !== "pf.identity.sex_as_documented"}>{chooseLabel}</option>
          {concept.id === "pf.identity.sex_as_documented" && value && !concept.answer.options.some(option => option.value === value) && <option value={value}>{value}</option>}
          {concept.answer.options.map((option) => (
            <option key={option.value} value={option.value}>{localized(option.label, locale)}</option>
          ))}
        </select>
      ) : concept.answer.shape === "calendar_date" ? (
        <input id={id} name={concept.id} type="date" value={value} {...inputProps} onChange={(event) => setAnswer(concept.id, event.target.value)} />
      ) : concept.answer.shape === "calendar_year" ? (
        <input id={id} name={concept.id} type="number" inputMode="numeric" min="1900" max={new Date().getFullYear() + 5} value={value} {...inputProps} onChange={(event) => setAnswer(concept.id, event.target.value)} />
      ) : concept.answer.shape === "date_interval" ? (
        <div id={id} className="date-interval" role="group" aria-label={label} tabIndex={-1}>
          <label><span>{locale === "es" ? "Inicio" : "Start"}</span><input type="date" {...inputProps} value={detail.start ?? ""} onChange={(event) => setAnswer(concept.id, {...detail, start: event.target.value})} /></label>
          <label><span>{locale === "es" ? "Fin" : "End"}</span><input type="date" {...inputProps} value={detail.end ?? ""} onChange={(event) => setAnswer(concept.id, {...detail, end: event.target.value})} /></label>
        </div>
      ) : concept.answer.shape === "monetary_amount" ? (
        <div id={id} className="money-control" role="group" aria-label={label} tabIndex={-1}>
          <input aria-label={locale === "es" ? "Importe" : "Amount"} type="number" inputMode="decimal" min="0" step="0.01" placeholder={locale === "es" ? "Importe" : "Amount"} {...inputProps} value={detail.amount ?? ""} onChange={(event) => setAnswer(concept.id, {...detail, amount: event.target.value})} />
          <input aria-label={locale === "es" ? "Moneda" : "Currency"} type="text" maxLength={3} placeholder="USD" {...inputProps} value={detail.currency ?? ""} onChange={(event) => setAnswer(concept.id, {...detail, currency: event.target.value.toUpperCase()})} />
        </div>
      ) : concept.answer.shape === "structured_entry" && concept.id === "pf.social.accounts" ? (
        <SocialProfileRepeater
          fields={concept.answer.entry_shape ?? []}
          locale={locale}
          profiles={socialProfiles ?? []}
          onProfilesChange={onSocialProfilesChange ?? (() => undefined)}
        />
      ) : concept.answer.shape === "structured_entry" && concept.answer.entry_shape?.length ? (
        <StructuredEntryRepeater fields={concept.answer.entry_shape} locale={locale} conceptId={concept.id} items={entries(current)} onItemsChange={(items) => setAnswer(concept.id, items)} />
      ) : concept.answer.shape === "structured_entry" ? (
        <RepeatingEntry label={label} locale={locale} conceptId={concept.id} items={Array.isArray(current) ? (current as unknown[]).filter((item): item is string => typeof item === "string") : []} onItemsChange={(items) => setAnswer(concept.id, items)} />
      ) : concept.answer.shape === "long_text" || concept.answer.cardinality === "many" ? (
        <ExpandingTextarea id={id} name={concept.id} className={concept.id === "pf.career.biography" ? "prose-large" : concept.id === "pf.engagement.duties" ? "prose-compact" : undefined} rows={concept.id === "pf.engagement.duties" ? 4 : undefined} autoCapitalize="sentences" value={value} {...inputProps} onChange={(event) => setAnswer(concept.id, event.target.value)} />
      ) : concept.answer.shape === "email_address" ? (
        <input id={id} name={concept.id} type="email" inputMode="email" autoComplete="email" value={value} {...inputProps} onChange={(event) => setAnswer(concept.id, event.target.value)} />
      ) : concept.answer.shape === "telephone_number" ? (
        <PhoneInput id={id} value={value} locale={locale} invalid={Boolean(fieldError)} required={required} onChange={(next) => setAnswer(concept.id, next)} />
      ) : concept.id === "pf.us.ssn" ? (
        <input id={id} name={concept.id} type="text" inputMode="numeric" autoComplete="off" maxLength={11} placeholder="XXX-XX-XXXX" value={value} {...inputProps} onChange={(event) => setAnswer(concept.id, event.target.value)} />
      ) : (
        <input
          id={id}
          name={concept.id}
          type="text"
          value={value}
          aria-invalid={invalid}
          onChange={(event) => setAnswer(concept.id, event.target.value)}
          autoCapitalize={[
            "pf.identity.given_names",
            "pf.identity.middle_names",
            "pf.identity.family_primary",
            "pf.identity.family_second",
            "pf.identity.birth_city",
            "pf.career.occupation",
          ].includes(concept.id) ? "words" : undefined}
        />
      )}
      {onOpenProfileAssistant && ["pf.career.biography", "pf.career.credits"].includes(concept.id) && (
        <button className="field-profile-assist" type="button" onClick={onOpenProfileAssistant}>
          <Sparkles size={16} aria-hidden="true" />
          <span>
            {concept.id === "pf.career.biography"
              ? (locale === "es" ? "Ayúdame a escribir mi perfil profesional" : "Help me write my professional profile")
              : (locale === "es" ? "Buscar créditos en sus fuentes" : "Find credits from your sources")}
          </span>
          <span aria-hidden="true">&gt;</span>
        </button>
      )}
      {errorMessage}
    </div>
  );
}

function OtherNamesQuestion({ locale, saveSignal, names, used, onUsedChange, onNamesChange }: { locale: ProductLocale; saveSignal: number; names: OtherName[]; used: "" | YesNo; onUsedChange: (value: YesNo) => void; onNamesChange: (names: OtherName[]) => void }) {
  const emptyName: OtherName = { givenName: "", middleName: "", familyName: "" };
  const answer: "" | YesNo = used === "yes" || used === "no" ? used : (names.length ? "yes" : "");
  const [draft, setDraft] = useFormDraft<OtherName>("other-names-draft", emptyName);
  const [editingIndex, setEditingIndex] = useFormDraft<number | null>("other-names-edit", null);
  const setNames: Dispatch<SetStateAction<OtherName[]>> = (next) => onNamesChange(typeof next === "function" ? next(names) : next);
  const [retainedNames, setRetainedNames] = useFormDraft<OtherName[]>("other-names-retained", []);
  const lastSaveSignal = useRef(saveSignal);
  const hasDraft = Object.values(draft).some((value) => value.trim());

  function saveDraft() {
    if (!hasDraft) return;
    setNames((current) => editingIndex === null
      ? [...current, draft]
      : current.map((name, index) => index === editingIndex ? draft : name));
    setDraft(emptyName);
    setEditingIndex(null);
  }

  useEffect(() => {
    if (saveSignal !== lastSaveSignal.current && answer === "yes") saveDraft();
    lastSaveSignal.current = saveSignal;
  }, [saveSignal]);

  function displayName(name: OtherName) {
    return [name.givenName, name.middleName, name.familyName].filter(Boolean).join(" ");
  }

  return (
    <fieldset className="question-field other-names-question">
      <legend>
        <div className="question-label-row">
          <span>{locale === "es" ? "¿Ha utilizado alguna vez otro nombre?" : "Have you ever used another name?"}</span>
          <details className="field-help">
            <summary aria-label={locale === "es" ? "Explicación de otros nombres" : "Other names explanation"}><CircleHelp size={17} /></summary>
            <p>{locale === "es" ? "Incluya nombres anteriores, de casada, profesionales, artísticos o alias." : "Include former, married, professional, stage, or alias names."}</p>
          </details>
        </div>
      </legend>
      <div className="choice-row">
        <label><input type="radio" name="other-names-used" value="yes" checked={answer === "yes"} onChange={() => { onUsedChange("yes"); if (!names.length && retainedNames.length) onNamesChange(retainedNames); }} />{locale === "es" ? "Sí" : "Yes"}</label>
        <label><input type="radio" name="other-names-used" value="no" checked={answer === "no"} onChange={() => { onUsedChange("no"); setRetainedNames(names); onNamesChange([]); }} />No</label>
      </div>
      {answer === "yes" && (
        <div className="other-names-entry">
          {names.length > 0 && (
            <div className="saved-entry-list other-names-list">
              {names.map((name, index) => (
                <details className="saved-entry" key={`${displayName(name)}-${index}`}>
                  <summary className="interaction-row"><span><strong>{displayName(name)}</strong></span><ChevronDown size={17} aria-hidden="true" /></summary>
                  <div className="saved-entry-content">
                    <div className="saved-entry-actions">
                      <button type="button" onClick={() => { setDraft(name); setEditingIndex(index); }}>{locale === "es" ? "Editar" : "Edit"}</button>
                      <button type="button" onClick={() => { setNames((current) => current.filter((_, nameIndex) => nameIndex !== index)); if (editingIndex === index) { setDraft(emptyName); setEditingIndex(null); } }}>{locale === "es" ? "Eliminar" : "Remove"}</button>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          )}
          <div className="other-name-fields">
            <label><span>{locale === "es" ? "Nombre (opcional)" : "First name (optional)"}</span><input type="text" autoCapitalize="words" value={draft.givenName} onChange={(event) => setDraft((current) => ({ ...current, givenName: event.target.value }))} /></label>
            <label><span>{locale === "es" ? "Segundo nombre (opcional)" : "Middle name (optional)"}</span><input type="text" autoCapitalize="words" value={draft.middleName} onChange={(event) => setDraft((current) => ({ ...current, middleName: event.target.value }))} /></label>
            <label><span>{locale === "es" ? "Apellido (opcional)" : "Last name (optional)"}</span><input type="text" autoCapitalize="words" value={draft.familyName} onChange={(event) => setDraft((current) => ({ ...current, familyName: event.target.value }))} /></label>
          </div>
          <button className="other-name-action" type="button" disabled={!hasDraft} onClick={saveDraft}>
            <Plus size={15} aria-hidden="true" />
            {editingIndex === null
              ? (locale === "es" ? "Añadir otro nombre" : "Add another name")
              : (locale === "es" ? "Guardar nombre" : "Save name")}
          </button>
        </div>
      )}
    </fieldset>
  );
}

function FamilyMemberRepeater({ locale, members, onMembersChange, travelPlans, onTravelPlansChange }: { travelPlans: string; onTravelPlansChange: (value: string) => void; locale: ProductLocale; members: FamilyMember[]; onMembersChange: (members: FamilyMember[]) => void }) {
  const emptyMember: FamilyMember = { givenNames: "", surnames: "", birthDate: "", passportCountry: "", relationship: "" };
  const [draft, setDraft] = useFormDraft<FamilyMember>("family-draft", emptyMember);
  const [editingIndex, setEditingIndex] = useFormDraft<number | null>("family-edit", null);
  const canAdd = Boolean((draft.legacyFullName?.trim() || draft.givenNames.trim() || draft.surnames.trim()) && draft.birthDate && draft.birthDate <= new Date().toLocaleDateString("en-CA") && draft.relationship.trim());
  const [adding, setAdding] = useState(false);
  const showForm = !members.length || adding || editingIndex !== null || Boolean(draft.legacyFullName || draft.givenNames || draft.surnames || draft.birthDate || draft.relationship);

  function addMember() {
    if (!canAdd) return;
    onMembersChange(editingIndex === null ? [...members, draft] : members.map((member, index) => index === editingIndex ? draft : member));
    setDraft(emptyMember);
    setEditingIndex(null);
    setAdding(false);
  }
  function removeMember(index: number) {
    onMembersChange(members.filter((_, memberIndex) => memberIndex !== index));
    if (editingIndex === index) { setEditingIndex(null); setDraft(emptyMember); }
    else if (editingIndex !== null && editingIndex > index) setEditingIndex(editingIndex - 1);
  }

  return (
    <div className="family-member-repeater">
      <p>{locale === "es" ? "Indique los familiares que podrían viajar con usted. Recopilaremos sus pasaportes y otros datos por separado." : "List any family members who may travel with you. We’ll collect their passport and other details separately."}</p>
      <label className="question-field"><span>{locale === "es" ? "Planes de viaje del grupo" : "Family travel plans"}</span>
        <select value={travelPlans} onChange={event => onTravelPlansChange(event.target.value)}>
          <option value="">{locale === "es" ? "Elegir" : "Choose"}</option>
          <option value="confirmed">{locale === "es" ? "Confirmados" : "Confirmed"}</option>
          <option value="considering">{locale === "es" ? "Por decidir" : "Still considering"}</option>
          <option value="mixed">{locale === "es" ? "Varían según el familiar" : "Plans differ by family member"}</option>
        </select>
      </label>
      {members.length > 0 && (
        <div className="saved-entry-list family-member-list">
          {members.map((member, index) => (
            <details className="saved-entry" key={`${[member.givenNames, member.surnames].filter(Boolean).join(" ") || member.legacyFullName}-${index}`}>
              <summary className="interaction-row"><span><strong>{[member.givenNames, member.surnames].filter(Boolean).join(" ") || member.legacyFullName}</strong><small>{member.relationship}</small></span><ChevronDown size={17} aria-hidden="true" /></summary>
              <div className="saved-entry-content">
                <dl>
                  <div><dt>{locale === "es" ? "Fecha de nacimiento" : "Date of birth"}</dt><dd>{member.birthDate}</dd></div>
                </dl>
                <button className="saved-entry-edit" type="button" onClick={() => { setDraft(member); setEditingIndex(index); }}>{locale === "es" ? "Editar" : "Edit"}</button>
                <button className="saved-entry-remove" type="button" onClick={() => removeMember(index)}>{locale === "es" ? "Eliminar" : "Remove"}</button>
              </div>
            </details>
          ))}
        </div>
      )}
      {showForm && <>
      <h3 className="family-entry-title">{editingIndex !== null ? (locale === "es" ? "Editar familiar" : "Edit family member") : (locale === "es" ? "Familiar" : "Family member")}</h3>
      <div className="family-member-fields">
        <label>
          <span>{locale === "es" ? "Nombre completo" : "Full name"}</span>
          <input type="text" autoCapitalize="words" value={draft.legacyFullName ?? [draft.givenNames, draft.surnames].filter(Boolean).join(" ")} onChange={event => setDraft(current => ({...current, legacyFullName: event.target.value, givenNames: "", surnames: ""}))} />
        </label>
        <label>
          <span>{locale === "es" ? "Fecha de nacimiento" : "Date of birth"}</span>
          <input type="date" max={new Date().toLocaleDateString("en-CA")} value={draft.birthDate} onChange={(event) => setDraft((current) => ({ ...current, birthDate: event.target.value }))} />
        </label>
        <label>
          <span>{locale === "es" ? "Parentesco" : "Relationship to you"}</span>
          <input type="text" autoCapitalize="words" value={draft.relationship} onChange={(event) => setDraft((current) => ({ ...current, relationship: event.target.value }))} />
        </label>
      </div>
      {editingIndex !== null && <button className="cancel-entry-edit" type="button" onClick={() => { setDraft(emptyMember); setEditingIndex(null); }}>{locale === "es" ? "Cancelar edición" : "Cancel editing"}</button>}
      <button className="add-family-member-button" type="button" disabled={!canAdd} onClick={addMember}>
        <Plus size={15} aria-hidden="true" />
        {editingIndex !== null ? (locale === "es" ? "Guardar cambios" : "Save changes") : (locale === "es" ? "Añadir familiar" : "Add family member")}
      </button>
      </>}
      {!showForm && <button className="add-family-member-button" type="button" onClick={() => { setDraft(emptyMember); setAdding(true); }}><Plus size={15} aria-hidden="true" />{locale === "es" ? "Añadir otro familiar" : "Add another family member"}</button>}
    </div>
  );
}

function StructuredEntryRepeater({ fields, locale, conceptId, items, onItemsChange }: { fields: ContractEntryField[]; locale: ProductLocale; conceptId: string; items: Entry[]; onItemsChange: (items: Entry[]) => void }) {
  const normalizedFields = fields.map((field) => {
    if (conceptId === "pf.career.credits" && field.id === "production_type") {
      return { ...field, label: { ...field.label, en: "Production type", es: "Tipo de producción" } };
    }
    if (conceptId === "pf.career.credits" && field.id === "production_type_other") {
      return { ...field, label: { ...field.label, en: "Other production type", es: "Otro tipo de producción" } };
    }
    return field;
  });
  const empty = Object.fromEntries(normalizedFields.map((field) => [field.id, ""]));
  const [draft, setDraft] = useFormDraft<Entry>(`${conceptId}-draft`, empty);
  const [editingIndex, setEditingIndex] = useFormDraft<number | null>(`${conceptId}-edit`, null);
  const visibleFields = normalizedFields.filter((field) => {
    if (!field.id.endsWith("_other")) return true;
    return draft[field.id.slice(0, -6)] === "other";
  });
  const canAdd = Object.values(draft).some((value) => value.trim()) && visibleFields.every((field) => field.requiredness === "optional" || draft[field.id]?.trim());

  const valueLabel = (field: ContractEntryField, value: string) => localized(field.answer.options?.find((option) => option.value === value)?.label, locale) || value;

  function update(id: string, value: string) {
    setDraft((current) => ({ ...current, [id]: value }));
  }

  function removeItem(index: number) {
    onItemsChange(items.filter((_, itemIndex) => itemIndex !== index));
    if (editingIndex === index) { setEditingIndex(null); setDraft(empty); }
    else if (editingIndex !== null && editingIndex > index) setEditingIndex(editingIndex - 1);
  }
  function addItem() {
    if (!canAdd) return;
    const entry = Object.fromEntries(visibleFields.map((field) => [field.id, draft[field.id] ?? ""]));
    onItemsChange(editingIndex === null ? [...items, entry] : items.map((item, index) => index === editingIndex ? entry : item));
    setDraft(empty);
    setEditingIndex(null);
  }

  function renderField(field: ContractEntryField) {
    const fieldLabel = optionalFieldLabel(localized(field.label, locale), field.requiredness === "optional", locale);
    const value = draft[field.id] ?? "";
    if (field.answer.shape === "single_choice") {
      return <select value={value} onChange={(event) => update(field.id, event.target.value)}><option value="">{locale === "es" ? "Elegir" : "Choose"}</option>{field.answer.options?.map((option) => <option key={option.value} value={option.value}>{localized(option.label, locale)}</option>)}</select>;
    }
    if (field.answer.shape === "country_reference") {
      return <CountryPicker id={`entry-${conceptId}-${field.id}`} label={fieldLabel} locale={locale} multiple={false} value={value ? [value] : []} onChange={(codes) => update(field.id, codes[0] ?? "")} />;
    }
    if (field.answer.shape === "calendar_date") return <input type="date" value={value} onChange={(event) => update(field.id, event.target.value)} />;
    if (field.answer.shape === "calendar_year") return <input type="number" inputMode="numeric" min="1900" max={new Date().getFullYear() + 5} value={value} onChange={(event) => update(field.id, event.target.value)} />;
    if (field.answer.shape === "long_text") return <ExpandingTextarea autoCapitalize="sentences" value={value} onChange={(event) => update(field.id, event.target.value)} />;
    if (field.answer.shape === "email_address") return <input type="email" inputMode="email" value={value} onChange={(event) => update(field.id, event.target.value)} />;
    const urlField = /url|link/.test(field.id);
    return <input aria-label={fieldLabel} type={urlField ? "url" : "text"} inputMode={urlField ? "url" : undefined} autoCapitalize={urlField ? "none" : ["production_title", "role", "director_or_producer"].includes(field.id) ? "words" : "sentences"} value={value} onChange={(event) => update(field.id, event.target.value)} />;
  }

  return (
    <div className="structured-entry-repeater">
      {items.length > 0 && <div className="saved-entry-list structured-entry-list">{items.map((item, index) => {
        const summary = normalizedFields.filter((field) => item[field.id]).slice(0, 2).map((field) => valueLabel(field, item[field.id])).join(", ");
        return <details className="saved-entry" key={`${summary}-${index}`}>
          <summary><span><strong>{summary}</strong></span><ChevronDown size={17} aria-hidden="true" /></summary>
          <div className="saved-entry-content">
            <dl>{normalizedFields.filter((field) => item[field.id]).map((field) => <div key={field.id}><dt>{localized(field.label, locale)}</dt><dd>{valueLabel(field, item[field.id])}</dd></div>)}</dl>
            <button className="saved-entry-edit" type="button" onClick={() => { setDraft({...empty, ...item}); setEditingIndex(index); }}>{locale === "es" ? "Editar" : "Edit"}</button>
            <button className="saved-entry-remove" type="button" onClick={() => removeItem(index)}>{locale === "es" ? "Eliminar" : "Remove"}</button>
          </div>
        </details>;
      })}</div>}
      <div className="structured-entry-fields">
        {visibleFields.map((field) => <label className={field.answer.shape === "long_text" ? "wide" : ""} key={field.id}><span>{optionalFieldLabel(localized(field.label, locale), field.requiredness === "optional", locale)}</span>{renderField(field)}</label>)}
      </div>
      {editingIndex !== null && <button className="cancel-entry-edit" type="button" onClick={() => { setDraft(empty); setEditingIndex(null); }}>{locale === "es" ? "Cancelar edición" : "Cancel editing"}</button>}
      <button className="add-entry-button" type="button" disabled={!canAdd} onClick={addItem}>
        <Plus size={15} aria-hidden="true" />
        {editingIndex !== null ? (locale === "es" ? "Guardar cambios" : "Save changes") : conceptId === "pf.career.credits"
          ? (locale === "es" ? "Añadir crédito" : "Add credit")
          : conceptId === "pf.passport.other_details"
            ? (locale === "es" ? "Añadir documento" : "Add document")
          : (locale === "es" ? "Añadir" : "Add")}
      </button>
    </div>
  );
}

const emptySocialProfile: SocialProfile = { platform: "", platformOther: "", handle: "", profileUrl: "", accountKind: "" };
function socialProfileValid(profile: SocialProfile) {
  return Boolean(profile.platform && (profile.handle.trim() || profile.profileUrl.trim()) && (profile.platform !== "other" || profile.platformOther.trim()) && (!(profile.platform in socialAccountFields) || parseSocialAccount(profile.platform, profile.profileUrl || profile.handle).valid));
}

function SocialProfileRepeater({ fields, locale, profiles, onProfilesChange }: { fields: ContractEntryField[]; locale: ProductLocale; profiles: SocialProfile[]; onProfilesChange: (profiles: SocialProfile[]) => void }) {
  const platformField = fields.find((field) => field.id === "platform");
  const accountKindField = fields.find((field) => field.id === "account_kind");
  const platformOptions = platformField?.answer.options ?? [];
  const selectablePlatforms = [
    { value: "facebook", label: "Facebook" },
    { value: "instagram", label: "Instagram" },
    { value: "tiktok", label: "TikTok" },
    { value: "x_twitter", label: "X" },
    { value: "youtube", label: "YouTube" },
  ];
  const accountKindOptions = accountKindField?.answer.options ?? [];
  const emptyProfile = emptySocialProfile;
  const [showErrors, setShowErrors] = useFormDraft<boolean>("social-errors", false);
  const [draft, setDraft] = useFormDraft<SocialProfile>("social-draft", emptyProfile);
  const [editingIndex, setEditingIndex] = useFormDraft<number | null>("social-edit", null);
  const [formOpen, setFormOpen] = useState(false);
  const showForm = formOpen || editingIndex !== null || profiles.length === 0 || Boolean(draft.platform || draft.handle || draft.profileUrl);
  const platformName = (profile: SocialProfile) => profile.platform === "other"
    ? profile.platformOther
    : selectablePlatforms.find((option) => option.value === profile.platform)?.label ?? localized(platformOptions.find((option) => option.value === profile.platform)?.label, locale);
  const canAdd = socialProfileValid(draft);
  const accountField = socialAccountFields[draft.platform as keyof typeof socialAccountFields];

  function removeProfile(index: number) {
    onProfilesChange(profiles.filter((_, itemIndex) => itemIndex !== index));
    if (editingIndex === index) { setDraft(emptyProfile); setEditingIndex(null); }
    else if (editingIndex !== null && editingIndex > index) setEditingIndex(editingIndex - 1);
  }

  function addProfile() {
    if (!canAdd) return;
    setShowErrors(false);
    onProfilesChange(editingIndex === null ? [...profiles, draft] : profiles.map((profile, index) => index === editingIndex ? draft : profile));
    setEditingIndex(null);
    setDraft(emptyProfile);
    setFormOpen(false);
  }

  return (
    <div className="social-profile-repeater">
      {profiles.length > 0 && (
        <div className="saved-entry-list social-profile-list">
          {profiles.map((profile, index) => (
            <div className="social-account-row" key={`${profile.platform}-${index}`}>
              <div className="social-account-identity"><strong>{platformName(profile)}</strong>
                <span>{profile.handle ? (['instagram', 'x_twitter', 'tiktok', 'youtube'].includes(profile.platform) ? `@${profile.handle.replace(/^@/, "")}` : profile.handle) : profile.profileUrl}</span>
                {profile.handle && profile.profileUrl && <small>{profile.profileUrl}</small>}
                {profile.accountKind && <small>{localized(accountKindOptions.find(option => option.value === profile.accountKind)?.label, locale)}</small>}
              </div>
              <button className="text-action" type="button" aria-label={`${locale === "es" ? "Editar" : "Edit"} ${platformName(profile)} ${profile.handle || profile.profileUrl}`} onClick={() => { setDraft(profile); setEditingIndex(index); setFormOpen(true); }}>{locale === "es" ? "Editar" : "Edit"}</button>
            </div>
          ))}
        </div>
      )}
      {showForm ? <div className="social-account-editor">

      {showErrors && !canAdd && <p className="social-validation" role="alert">{locale === "es" ? "Elija una plataforma e indique un usuario o enlace de perfil válido de esa plataforma." : "Choose a platform and enter a valid username or profile link for that platform."}</p>}
      <div className="social-profile-fields">
        <label>
          <span>{locale === "es" ? "Plataforma" : "Platform"}</span>
          <select id="social-platform" aria-invalid={showErrors && !draft.platform} value={draft.platform} onChange={(event) => { const platform = event.target.value; setDraft((current) => { const parsed = parseSocialAccount(platform, current.profileUrl || current.handle); return { ...current, platform, handle: parsed.profileUrl ? parsed.handle : current.handle, profileUrl: current.profileUrl }; }); }}>
            <option value="">{locale === "es" ? "Elegir" : "Choose"}</option>
            {draft.platform && !selectablePlatforms.some((option) => option.value === draft.platform) && <option value={draft.platform} hidden>{platformName(draft)}</option>}
            {selectablePlatforms.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        {draft.platform === "other" && (
          <label>
            <span>{locale === "es" ? "Nombre de la plataforma" : "Platform name"}</span>
            <input id="social-platform-other" aria-invalid={showErrors && !draft.platformOther.trim()} autoCapitalize="words" value={draft.platformOther} onChange={(event) => setDraft((current) => ({ ...current, platformOther: event.target.value }))} />
          </label>
        )}
        {accountField ? <label>
          <span>{accountField[locale]}</span>
          <input id="social-handle" aria-invalid={showErrors && !canAdd} autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder={accountField.example.replace(" or ", locale === "es" ? " o " : " or ")} value={draft.profileUrl || draft.handle} onChange={(event) => {
            const input = event.target.value;
            const parsed = parseSocialAccount(draft.platform, input);
            setDraft((current) => ({ ...current, handle: parsed.profileUrl ? parsed.handle : input, profileUrl: parsed.profileUrl ? input : "" }));
          }} onBlur={() => setShowErrors(true)} />
        </label> : draft.platform ? <>
        <label>
          <span>{locale === "es" ? "Nombre de usuario" : "Handle or username"}</span>
          <input id="social-handle" aria-invalid={showErrors && !draft.handle.trim() && !draft.profileUrl.trim()} autoCapitalize="none" autoCorrect="off" spellCheck={false} value={draft.handle} onChange={(event) => setDraft((current) => ({ ...current, handle: event.target.value }))} />
        </label>
        <label>
          <span>{locale === "es" ? "Enlace del perfil" : "Profile link"}</span>
          <input type="url" inputMode="url" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={draft.profileUrl} onChange={(event) => setDraft((current) => ({ ...current, profileUrl: event.target.value }))} />
        </label>
        </> : null}
        <label>
          <span>{locale === "es" ? "Tipo de cuenta (opcional)" : "Account type (optional)"}</span>
          <select value={draft.accountKind} onChange={(event) => setDraft((current) => ({ ...current, accountKind: event.target.value }))}>
            <option value="">{locale === "es" ? "Elegir" : "Choose"}</option>
            {accountKindOptions.map((option) => <option key={option.value} value={option.value}>{localized(option.label, locale)}</option>)}
          </select>
        </label>
      </div>
      <button className="add-profile-button" type="button" disabled={!canAdd} onClick={addProfile}>
        {editingIndex === null && <Plus size={15} aria-hidden="true" />}
        {editingIndex !== null ? (locale === "es" ? "Guardar cambios" : "Save changes") : (locale === "es" ? "Añadir perfil" : "Add profile")}
      </button>
      <div className="social-editor-secondary">
        <button className="text-action" type="button" onClick={() => { setDraft(emptyProfile); setEditingIndex(null); setFormOpen(false); }}>{locale === "es" ? "Cancelar" : "Cancel"}</button>
        {editingIndex !== null && <button className="text-action" type="button" onClick={() => { removeProfile(editingIndex); setFormOpen(false); }}>{locale === "es" ? "Eliminar cuenta" : "Remove account"}</button>}
      </div>
      </div> : <button className="add-profile-button" type="button" onClick={() => setFormOpen(true)}><Plus size={18} aria-hidden="true" />{locale === "es" ? "Añadir otra cuenta" : "Add another account"}</button>}
    </div>
  );
}

/** A resume stored for the professional profile (binary in S3, record + extracted text in Mongo). */
type ResumeFile = { id: string; name: string };

function ProfessionalProfileAssistant({
  locale, step, sourceText, sourceFiles, uploadingFiles, fetchingXAccount, generating, draft, profiles, selectedProfileSources,
  onDiscard, onStepChange, onSourceTextChange, onUploadFiles, onRemoveFile, onUseXAccount,
  onDraftChange, onProfilesChange, onSelectedProfileSourcesChange, onRegenerate,
}: {
  locale: ProductLocale; step: number; sourceText: string; sourceFiles: ResumeFile[]; uploadingFiles: string[]; fetchingXAccount: boolean;
  generating: boolean; draft: string; profiles: SocialProfile[]; selectedProfileSources: string[];
  onDiscard: () => void; onStepChange: (step: number) => void;
  onSourceTextChange: (value: string) => void; onUploadFiles: (files: File[]) => void; onRemoveFile: (file: ResumeFile) => void;
  onUseXAccount: (handle: string) => void;
  onDraftChange: (value: string) => void; onProfilesChange: (profiles: SocialProfile[]) => void;
  onSelectedProfileSourcesChange: (urls: string[]) => void;
  onRegenerate: () => void;
}) {
  const es = locale === "es";
  const fileInputRef = useRef<HTMLInputElement>(null);
  const savedX = profiles.find(profile => profile.platform === "x_twitter" && selectedProfileSources.includes(profile.profileUrl)) ?? profiles.find(profile => profile.platform === "x_twitter");
  const [changingAccount, setChangingAccount] = useState(false);
  const [xHandle, setXHandle] = useState(savedX?.handle ?? "");
  const [xError, setXError] = useState("");
  function saveXAccount() {
    const handle = xHandle.trim().replace(/^@/, "");
    if (!/^[A-Za-z0-9_]{1,15}$/.test(handle)) { setXError(es ? "Introduzca un nombre de usuario de X válido." : "Enter a valid X handle."); return; }
    const profileUrl = `https://x.com/${handle}`;
    const existing = profiles.find(profile => profile.platform === "x_twitter" && profile.handle.replace(/^@/, "").toLowerCase() === handle.toLowerCase());
    // Keep disclosure accounts intact; only one account is selected for generation.
    if (!existing) onProfilesChange([...profiles, { platform: "x_twitter", platformOther: "", handle, profileUrl, accountKind: "" }]);
    onSelectedProfileSourcesChange([existing?.profileUrl || profileUrl]);
    setChangingAccount(false);
    onUseXAccount(handle);
  }
  function addFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length) onUploadFiles(files);
  }
  return <div className="profile-builder">
    {step === 5 && <section className="profile-source-choice">
      <div className="profile-choice-intro"><h3>{es ? "¿Cómo quiere empezar?" : "How would you like to start?"}</h3><p>{es ? "Elija qué usar para crear su perfil. Podrá editar el borrador antes de guardarlo." : "Choose what to use for your profile. You can edit the draft before saving it."}</p></div>
      <div className="profile-choice-rows">
        <button className="interaction-row interaction-row-with-icon" type="button" onClick={() => onStepChange(0)}><Upload size={22} aria-hidden="true" /><span><strong>{es ? "Subir su currículum" : "Upload your resume"}</strong></span><ChevronRight size={18} aria-hidden="true" /></button>
        <button className="interaction-row interaction-row-with-icon" type="button" onClick={() => onStepChange(1)}><FileText size={22} aria-hidden="true" /><span><strong>{es ? "Describir su trabajo" : "Write about your work"}</strong></span><ChevronRight size={18} aria-hidden="true" /></button>
        <button className="interaction-row interaction-row-with-icon" type="button" onClick={() => onStepChange(2)}><span className="x-source-icon" aria-hidden="true">𝕏</span><span><strong>{es ? "Usar su cuenta de X" : "Use your X account"}</strong><small>{es ? "Cree un perfil a partir de su cuenta pública." : "Create a profile from your public account."}</small></span><ChevronRight size={18} aria-hidden="true" /></button>
      </div>
    </section>}
    {step === 0 && <section className="assistant-guided-step">
      <h3>{es ? "Subir su currículum" : "Upload your resume"}</h3>
      <p className="assistant-source-guidance">{es ? "Elija un archivo. A continuación podrá revisar el borrador." : "Choose a file. You’ll review the draft next."}</p>
      <div className="assistant-source-panel assistant-files"><button type="button" disabled={uploadingFiles.length > 0} onClick={() => fileInputRef.current?.click()}><Upload size={22} aria-hidden="true" />{uploadingFiles.length > 0 ? (es ? "Subiendo…" : "Uploading…") : (es ? "Elegir archivo" : "Choose file")}<small>PDF, Word {es ? "o texto" : "or text"}</small></button><input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.txt" hidden onChange={addFiles} />
      {(sourceFiles.length > 0 || uploadingFiles.length > 0) && <div className="assistant-source-list">
        {sourceFiles.map(file => <div key={file.id}><FileText size={17} aria-hidden="true" /><span>{file.name}</span><button type="button" aria-label={`${es ? "Eliminar" : "Remove"} ${file.name}`} onClick={() => onRemoveFile(file)}><X size={17} /></button></div>)}
        {uploadingFiles.map(name => <div key={`uploading-${name}`} aria-busy="true"><FileText size={17} aria-hidden="true" /><span>{name}<small>{es ? "Subiendo…" : "Uploading…"}</small></span><span /></div>)}
      </div>}</div>
    </section>}
    {step === 1 && <section className="assistant-guided-step"><h3>{es ? "Experiencia profesional" : "Professional experience"}</h3><p className="assistant-source-guidance">{es ? "Escriba o pegue información sobre sus funciones, producciones, logros y reconocimientos." : "Describe your roles, productions, achievements, and recognition. You can write here or paste text you already have."}</p><label className="assistant-paste"><span className="sr-only">{es ? "Su experiencia profesional" : "Your professional experience"}</span><ExpandingTextarea value={sourceText} onChange={event => onSourceTextChange(event.target.value)} placeholder={es ? "Experiencia profesional…" : "Professional experience…"} /></label></section>}
    {step === 2 && <section className="assistant-guided-step">
      <h3>{savedX && !changingAccount ? (es ? "¿Usar esta cuenta para crear su perfil?" : "Use this account to create your profile?") : (es ? "Introduzca su nombre de usuario de X" : "Enter your X username")}</h3>
      {savedX && !changingAccount ? <div className="saved-x-source"><strong>{savedX.handle ? `@${savedX.handle.replace(/^@/, "")}` : savedX.profileUrl}</strong><button type="button" className="text-action" onClick={() => { setXHandle(savedX.handle); setChangingAccount(true); }}>{es ? "Cambiar cuenta" : "Change account"}</button></div> : <><label className="assistant-x-label" htmlFor="profile-x-handle">{es ? "Nombre de usuario en X" : "X handle"}</label><div className="assistant-x-input"><span aria-hidden="true">@</span><input id="profile-x-handle" value={xHandle} autoCapitalize="none" autoCorrect="off" spellCheck={false} onChange={event => { setXHandle(event.target.value.replace(/^@/, "")); setXError(""); }} placeholder={es ? "usuario" : "username"} aria-invalid={Boolean(xError)} /></div></>}
      <p className="assistant-source-guidance">{es ? "Usaremos Grok para recopilar información pública relevante de esta cuenta y proponer su perfil." : "We’ll use Grok to gather relevant public information from this account and propose your profile."}</p>
      {xError && <p role="alert">{xError}</p>}
      {savedX && !changingAccount ? <button type="button" className="x-use-account" disabled={fetchingXAccount} onClick={() => { const handle = savedX.handle.replace(/^@/, "") || savedX.profileUrl.replace(/^https?:\/\/(?:www\.)?(?:x|twitter)\.com\//i, "").replace(/\/.*$/, ""); onSelectedProfileSourcesChange([savedX.profileUrl || `https://x.com/${handle}`]); onUseXAccount(handle); }}>{fetchingXAccount ? (es ? "Leyendo la cuenta…" : "Reading account…") : (es ? "Usar esta cuenta" : "Use this account")}</button> : <button type="button" className="x-use-account" disabled={fetchingXAccount} onClick={saveXAccount}>{fetchingXAccount ? (es ? "Leyendo la cuenta…" : "Reading account…") : (es ? "Usar esta cuenta" : "Use this account")}</button>}
    </section>}
    {step === 4 && <div className="profile-assistant-review editing">
      <label id="professional-profile-label" htmlFor="professional-profile-draft">{es ? "Revise su perfil" : "Review your profile"}</label>
      {generating ? <p className="assistant-source-guidance" aria-live="polite">{es ? "Creando su perfil a partir de sus fuentes…" : "Creating your profile from your sources…"}</p> : <p className="assistant-source-guidance">{es ? "Edite su perfil antes de guardarlo." : "Edit your profile before saving."}</p>}
      <ExpandingTextarea id="professional-profile-draft" aria-labelledby="professional-profile-label" value={draft} disabled={generating} onChange={event => onDraftChange(event.target.value)} placeholder={generating ? (es ? "Creando su perfil…" : "Creating your profile…") : (es ? "Su borrador aparecerá aquí." : "Your draft will appear here.")} />
      {!generating && !draft.trim() && <p className="assistant-source-guidance">{es ? "No se pudo crear un perfil a partir de esa fuente. Añada más detalle o pruebe otra fuente." : "A profile could not be created from that source. Add more detail or try another source."}</p>}
      <button className="regenerate-profile" type="button" disabled={generating} onClick={onRegenerate}><RotateCw size={18} aria-hidden="true" />{generating ? (es ? "Creando…" : "Creating…") : (es ? "Probar otra versión" : "Try another version")}</button>
      <div className="assistant-review-actions"><button className="assistant-review-discard" type="button" onClick={onDiscard}>{es ? "Descartar este borrador" : "Discard this draft"}</button></div>
    </div>}
  </div>;
}

function ContactDetail({ concept, locale, answers, setAnswer, error }: {
  concept: ContractConcept; locale: ProductLocale; answers: Answers;
  setAnswer: (id: string, value: AnswerValue) => void; error?: string;
}) {
  // Snapshot only when this screen opens, so typing into an empty field never
  // replaces the focused input with a summary after the first keystroke.
  const [hasExistingValue] = useState(() => Boolean(scalar(answers[concept.id])));
  const value = scalar(answers[concept.id]);
  if (concept.id === "pf.contact.email" || concept.id === "pf.contact.phone") return <VerifiedContact validationError={error} kind={concept.id === "pf.contact.email" ? "email" : "phone"} value={value} currentEmail={scalar(answers["pf.contact.email"])} locale={locale} onVerified={next => setAnswer(concept.id, next)} />;
  const field = <div className={concept.id === "pf.contact.email" ? "contact-email-field" : concept.id === "pf.contact.phone" ? "contact-phone-field" : undefined}><ContractQuestion concept={concept} locale={locale} answers={answers} setAnswer={setAnswer} error={error} /></div>;
  if (!["pf.contact.email", "pf.contact.phone", "pf.contact.language"].includes(concept.id) || !hasExistingValue || error) return field;
  const displayValue = concept.id === "pf.contact.language" ? (value === "es" ? "Español" : value === "en" ? "English" : value) : value;
  return <details className="contact-summary">
    <summary><span><span>{localized(concept.label, locale)}</span><strong>{displayValue}</strong></span><span className="contact-edit-label">{locale === "es" ? "Editar" : "Edit"}<ChevronDown size={16} aria-hidden="true" /></span></summary>
    <div>{field}</div>
  </details>;
}

function RepeatingEntry({ label, locale, conceptId, items, onItemsChange }: { label: string; locale: ProductLocale; conceptId: string; items: string[]; onItemsChange: (items: string[]) => void }) {
  const [value, setValue] = useFormDraft<string>(`${conceptId}-draft`, "");
  const setItems: Dispatch<SetStateAction<string[]>> = (next) => onItemsChange(typeof next === "function" ? next(items) : next);
  return (
    <div className="repeating-entry">
      {items.length > 0 && (
        <ul>{items.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>
      )}
      <div>
        <input aria-label={label} value={value} onChange={(event) => setValue(event.target.value)} />
        <button type="button" disabled={!value.trim()} onClick={() => { setItems((current) => [...current, value.trim()]); setValue(""); }}>
          {locale === "es" ? "Añadir" : "Add"}
        </button>
      </div>
    </div>
  );
}

type DisplayDocument = DocumentRequest & {parentId?: string; retained?: boolean};
type CaseDocumentRequest = { status: "requested"; instructions: { en: string; es?: string } };
type CaseDocumentRequests = Record<string, CaseDocumentRequest>;
const documentText = documentCopy as Record<string, {title: {en: string; es: string}; description: {en: string; es: string}}>;

function DocumentList({documents, locale, files, passportFileName, onChoose, onPassport, onOtherTravelDocuments, answers, requests, responses, onResponse, savedDocuments, onReview}: {
  answers: Answers;
  documents: DocumentRequest[];
  locale: ProductLocale;
  files: Record<string, string>;
  passportFileName: string;
  requests: CaseDocumentRequests;
  responses: Record<string, string>;
  onResponse: (document: DisplayDocument, reason: string) => void;
  savedDocuments: Record<string, DisplayDocument>;
  onReview: (task: "professional_profile" | "accompanying_family") => void;
  onChoose: (document: DocumentRequest) => void;
  onPassport: () => void;
  onOtherTravelDocuments: () => void;
}) {
  const {experience} = useExperience();
  const [visaInOldPassport, setVisaInOldPassport] = useState(false);
  const [responding, setResponding] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const personalized: {documents: DisplayDocument[]; pending: {document: DocumentRequest; task: "professional_profile" | "accompanying_family"}[]} = personalizeDocuments(documents, answers, requests, savedDocuments, files, responses);
  const items: {document: DisplayDocument; request?: CaseDocumentRequest; state: string}[] = documentItems(personalized.documents, (document: DisplayDocument) => document.retained ? false : applicability(document, answers, experience.concepts), requests,
    {...files, ...Object.fromEntries(documents.filter(d => d.id.endsWith(".passport") && passportFileName).map(d => [d.id, passportFileName]))}, responses);
  const visible = items.filter(item => item.state === "requested" || item.state === "retained");
  return <div className="document-list">
    {experience.document_groups.map(group => {
      const groupItems = visible.filter(item => group.ids.includes(item.document.parentId ?? item.document.id));
      if (!groupItems.length) return null;
      return <section className="document-group" key={group.ids[0]} aria-label={groupItems.length > 1 ? (locale === "es" ? "Documentos solicitados" : "Requested documents") : undefined}>
        <div className="document-group-list">{groupItems.map(({document, request, state}) => {
          const isPassport = document.id.endsWith(".passport");
          const fileName = isPassport ? passportFileName : files[document.id];
          const copy = document.parentId ? undefined : documentText[document.id];
          return <details className="document-row interaction-row-group" key={document.id}>
            <summary className="interaction-row">
              <span>{isPassport ? (locale === "es" ? "Pasaporte" : "Passport") : copy?.title[locale] ?? localized(document.title, locale)}</span>
              <span className="document-row-controls">
                {responses[document.id] && <span className="document-status">{locale === "es" ? "No disponible" : "Unavailable"}</span>}
                {fileName && <span className="document-status">{locale === "es" ? "Archivo seleccionado" : "File selected"}</span>}
                <ChevronDown className="document-row-chevron" size={18} aria-hidden="true" />
              </span>
            </summary>
            <div className="document-row-content">
              {state === "retained" ? <p>{locale === "es" ? "Su archivo o respuesta anterior se conserva. Este documento no se solicita actualmente." : "Your previous file or response is retained. This document is not currently requested."}</p> : <p>{request?.instructions?.[locale] || request?.instructions?.en || (isPassport ? (locale === "es" ? "Suba todas las páginas, incluidas las páginas en blanco, en un solo PDF en color." : "Upload all pages, including blank pages, together in one color PDF.") : copy?.description[locale] ?? localized(document.description, locale))}</p>}
              {document.id === "pdoc.us.current_status_records" && <div className="passport-exception">
                <label><input type="checkbox" checked={visaInOldPassport} onChange={event => setVisaInOldPassport(event.target.checked)} /> {locale === "es" ? "Mi visa estadounidense vigente está en un pasaporte anterior" : "My valid U.S. visa is in an old passport"}</label>
                {visaInOldPassport && <button type="button" className="text-action" onClick={onOtherTravelDocuments}>{locale === "es" ? "Añadir el pasaporte anterior" : "Add the old passport"}</button>}
              </div>}
              {fileName && <span className="document-file-name"><FileText size={15} />{fileName}</span>}
              {responses[document.id] && <div className="document-response"><p>{responses[document.id]}</p><button type="button" className="text-action" onClick={() => onResponse(document, "")}>{locale === "es" ? "Eliminar respuesta" : "Remove response"}</button></div>}
              <div className="document-actions">
                <button type="button" className={`document-action ${isPassport ? "passport" : fileName ? "replace" : "add"}`} onClick={() => isPassport ? onPassport() : onChoose(document)}>
                  <Upload size={16} />{isPassport ? (locale === "es" ? "Subir PDF" : "Upload PDF") : fileName ? (locale === "es" ? "Reemplazar archivo" : "Replace file") : (locale === "es" ? "Añadir archivo" : "Add file")}
                </button>
                {!isPassport && state === "requested" && <button type="button" className="text-action" onClick={() => {setResponding(document.id); setReason(responses[document.id] ?? "");}}>{responses[document.id] ? (locale === "es" ? "Editar respuesta" : "Edit response") : (locale === "es" ? "No puedo proporcionar esto" : "I can’t provide this")}</button>}
              </div>
              {responding === document.id && <div className="document-response-form">
                <label htmlFor={`reason-${document.id}`}>{locale === "es" ? "¿Por qué no está disponible?" : "Why is it unavailable?"}</label>
                <ExpandingTextarea id={`reason-${document.id}`} value={reason} onChange={event => setReason(event.target.value)} rows={3} />
                <div className="document-actions"><button type="button" disabled={!reason.trim()} onClick={() => {onResponse(document, reason.trim()); setResponding(null);}}>{locale === "es" ? "Guardar respuesta" : "Save response"}</button><button type="button" className="text-action" onClick={() => setResponding(null)}>{locale === "es" ? "Cancelar" : "Cancel"}</button></div>
              </div>}
            </div>
          </details>;
        })}</div>
      </section>;
    })}
    {personalized.pending.filter(item => applicability(item.document, answers, experience.concepts) === true).map(item => <div className="document-next-step" key={item.document.id}>
      <p>{item.task === "professional_profile" ? (locale === "es" ? "Revise los premios, publicaciones, resultados y remuneración de su perfil para que podamos identificar los documentos correspondientes." : "Review the awards, publications, results, and pay records in your profile so we can identify the supporting documents.") : (locale === "es" ? "Añada los nombres de los familiares que le acompañan para identificar sus documentos." : "Add the names of accompanying family members so we can identify their documents.")}</p>
      <button type="button" className="text-action" onClick={() => onReview(item.task)}>{item.task === "professional_profile" ? (locale === "es" ? "Revisar perfil" : "Review profile") : (locale === "es" ? "Revisar familiares" : "Review family members")}</button>
    </div>)}
    {(items.some(item => item.state === "unresolved") || documents.some(document => applicability(document, answers, experience.concepts) === "unknown")) && <p className="document-pending-note">{locale === "es" ? "Si se confirma que necesita otros documentos, se solicitarán aquí." : "Any additional document requests will appear here once confirmed."}</p>}
  </div>;
}

function PassportActions({
  reviewed,
  locale,
  fileName,
  currentCountry,
  onUpload,
  onFiles,
  onRemove,
  onMissing,
  onExpiring,
  onContinue,
}: {
  reviewed: boolean;
  onContinue: () => void;
  onMissing: () => void;
  onExpiring: () => void;
  locale: ProductLocale;
  fileName: string;
  currentCountry: string;
  onUpload: () => void;
  onFiles: (files: File[]) => void;
  onRemove: () => void;
}) {
  return (
    <div className="passport-actions">
      <section className="passport-upload-area" aria-label={locale === "es" ? "Subir pasaporte" : "Passport upload"}>
      <div className="passport-upload-layout">
      <div className="passport-upload-copy">
      {!fileName && <p className="passport-upload-intro">{locale === "es" ? "Suba todas las páginas de su pasaporte en un solo PDF. Completaremos los datos para que los revise." : "Upload all pages of your passport in one PDF. We’ll fill in the details for you to review."}</p>}
      {fileName && <p className="passport-file-requirement">{locale === "es" ? "Incluya todas las páginas, incluidas las páginas en blanco, en un solo PDF." : "Include all pages, including blank pages, in one PDF."}</p>}
      {fileName && <div className="passport-file">
        <FileCheck2 size={18} aria-hidden="true" />
        <span>{fileName}</span>
        <button type="button" aria-label={locale === "es" ? "Eliminar archivo del pasaporte" : "Remove passport file"} onClick={onRemove}><X size={18} /></button>
      </div>}
      </div>
      <PassportPdfDropZone fileName={fileName} es={locale === "es"} onChoose={onUpload} onFiles={onFiles} />
      </div>
      <PassportScanHelp locale={locale} initialCountry={currentCountry} />
      </section>
      {fileName ? <p className="passport-extraction-note">{locale === "es" ? "Leeremos este PDF y rellenaremos los datos para que los revise. También puede introducirlos en las pantallas siguientes." : "We’ll read this PDF and fill in the details for you to review. You can also enter them on the next screens."}</p> : <div className="passport-enter-later">
        <button type="button" className="passport-details-button interaction-row" onClick={onContinue}>{reviewed ? (locale === "es" ? "Revisar datos del pasaporte" : "Review passport details") : (locale === "es" ? "Introducir datos del pasaporte manualmente" : "Enter passport details manually")}<ChevronRight size={18} aria-hidden="true" /></button>
      </div>}
      <div className="passport-issue-actions">
      <button type="button" className="passport-missing-action interaction-row" onClick={onMissing}><span>{locale === "es" ? "No tengo un pasaporte válido" : "I don’t have a valid passport"}</span><ChevronRight size={18} aria-hidden="true" /></button>
      <button type="button" className="passport-missing-action interaction-row" onClick={onExpiring}><span>{locale === "es" ? "Mi pasaporte vence pronto" : "My passport expires soon"}</span><ChevronRight size={18} aria-hidden="true" /></button>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [drafts, setDrafts] = useState<Record<string, unknown>>({});
  const [account, setAccount] = useState<ParticipantAccount | null>(null);
  const [inputs, setInputs] = useState<Record<string, unknown> | null>(null);
  const lastExperience = useRef<Experience | null>(null);
  useEffect(() => {
    let cancelled = false;
    loadParticipantAccount().then((loaded) => {
      if (cancelled) return;
      setAccount(loaded);
      setInputs(loaded.experienceInputs);
    });
    return () => { cancelled = true; };
  }, []);
  if (!inputs || !account) {
    return <main className="app-shell"><p className="invitation-intro">Loading your workspace…</p></main>;
  }
  const {result, experience} = resolveExperience(inputs);
  if (experience) lastExperience.current = experience;
  // Keep the workspace mounted so a routing change does not erase drafts.
  // Its forms are hidden when no intake is selected; the fallback is never a routing result.
  const displayedExperience = experience ?? lastExperience.current ?? resolveExperience(demoInputsFromSearch("")).experience!;
  const supplied = suppliedExperienceAnswers(result.inputs, displayedExperience);
  const routingMessage = experience ? null : result.message ?? ((result.missing ?? []).includes("es_unplanned_extension_confirmed")
    ? "An advisor needs to review this in-country extension before an intake can be selected."
    : (result.missing ?? []).length
      ? "Your production country and passport country are needed before these forms can open."
      : "These forms are not available for this production yet.");
  function updateDemoContext(facts: Record<string, unknown>) {
    if (!DEMO_PASSPORT_SWITCHER) return;
    const next = {...inputs, ...facts};
    if (next.passport_country === next.destination_country) next.passport_country = "CO";
    setInputs(next);
    const url = new URL(window.location.href);
    const keys = {passport_country: "passport", destination_country: "destination", production_kind: "production", employment_relationship: "employment", stay_days: "days", current_country: "current", accompanies_principal_o1: "support", principal_o1_id: "principal", es_eu_rights_exception: "es_eu_rights_exception", es_unplanned_extension_confirmed: "es_unplanned_extension_confirmed", es_lawful_stay_confirmed: "es_lawful_stay_confirmed", es_schengen_allowance_confirmed: "es_schengen_allowance_confirmed"};
    for (const [key, parameter] of Object.entries(keys)) {
      if (next[key] === undefined || next[key] === "") url.searchParams.delete(parameter);
      else url.searchParams.set(parameter, String(next[key]));
    }
    window.history.replaceState({}, "", url);
  }
  return <ExperienceContext.Provider value={{experience: displayedExperience, supplied, account, onFactsChange: (facts) => setInputs((current) => ({...current, ...facts})), demoContext: inputs, onDemoChange: updateDemoContext, routingMessage}}><FormDraftContext.Provider value={{drafts, setDrafts}}><ParticipantWorkspace /></FormDraftContext.Provider></ExperienceContext.Provider>;
}

/** Fills answers that are still empty with values saved on the server, without overriding local edits. */
function fillEmptyAnswers(current: Answers, saved: Answers): Answers {
  const next = { ...current };
  for (const [id, value] of Object.entries(saved)) {
    if (Array.isArray(value)) {
      if (!entries(current[id]).length) next[id] = value;
    } else if (!scalar(current[id]).trim()) {
      next[id] = value;
    }
  }
  return next;
}

function ParticipantWorkspace({caseDocumentRequests = {}}: {caseDocumentRequests?: CaseDocumentRequests} = {}) {
  const formDrafts = useContext(FormDraftContext);
  const {experience, supplied, account, onFactsChange, demoContext, onDemoChange, routingMessage} = useExperience();
  const suppliedKey = JSON.stringify(supplied);
  const previousSupplied = useRef(supplied);
  const allConcepts = experience.concepts;
  const passportInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const [savingProfile, setSavingProfile] = useState<string | null>(null);
  const [passportUploadResult, setPassportUploadResult] = useState<{state:string; fields:Record<string,string>; fileName:string; uploaded?:boolean} | null>(null);
  const passportDemoAttempt = useRef(0);
  const [passportVerifiedPreview, setPassportVerifiedPreview] = useState(false);
  const [passportFileName, setPassportFileName] = useState<string>("");
  const [passportStorageKey, setPassportStorageKey] = useState<string>("");
  const [passportScanning, setPassportScanning] = useState(false);
  const [passportExpiringFlow, setPassportExpiringFlow] = useState(false);
  const [passportAvailability, setPassportAvailability] = useState<PassportAvailability>(emptyPassportAvailability);
  const [passportExceptionDrafts, setPassportExceptionDrafts] = useState<Record<string, PassportAvailability>>({});
  const [passportRequestReason, setPassportRequestReason] = useState("");
  const [passportAvailabilityStep, setPassportAvailabilityStep] = useState(0);
  const [showPassportAvailability, setShowPassportAvailability] = useState(false);
  const openPassportIssue = passportIssue(passportAvailability, null, typeof demoContext.first_location_date === "string" ? demoContext.first_location_date : "");
  const [pendingDocument, setPendingDocument] = useState<DocumentRequest | null>(null);
  const [savedDocuments, setSavedDocuments] = useState<Record<string, DisplayDocument>>({});
  const [documentFiles, setDocumentFiles] = useState<Record<string, string>>({});
  const [documentResponses, setDocumentResponses] = useState<Record<string, string>>({});
  const [answers, setAnswers] = useState<Answers>(() => ({...supplied, "pf.contact.language": "en"}));
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const [demoFillEnabled, setDemoFillEnabled] = useState(true);
  const insertedDemoAnswers = useRef<Answers>({});
  useEffect(() => {
    if (!DEMO_PASSPORT_SWITCHER) return;
    setAnswers(current => {
      const clean = removeSamples(current, insertedDemoAnswers.current);
      const filled = demoFillEnabled ? fillSamples(clean, sampleAnswers(experience.concepts, demoContext)) : {answers: clean, inserted: {}};
      insertedDemoAnswers.current = filled.inserted;
      return filled.answers;
    });
    setFormErrors({});
    setCompletedTasks(new Set());
    setReviewedTasks(new Set());
  }, [demoFillEnabled, experience.id]);
  const editedDefaultFields = useRef(new Set<string>());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [workStep, setWorkStep] = useState(0);
  const [personalTab, setPersonalTab] = useState<"contact" | "address" | "names">("contact");
  const [notice, setNotice] = useState<Notice | null>(null);
  const [activeTask, setActiveTask] = useState<string | null>(null);
  const workspaceIntroductionKey = "gm.participant.hideWorkspaceIntroduction.v1";
  const [workspaceIntroduction, setWorkspaceIntroduction] = useState(() => {
    try { return localStorage.getItem(workspaceIntroductionKey) !== "true" || new URLSearchParams(window.location.search).get("intro") === "1"; }
    catch { return true; }
  });
  const [hideWorkspaceIntroduction, setHideWorkspaceIntroduction] = useState(false);
  const [workspaceIntroductionSeen, setWorkspaceIntroductionSeen] = useState(false);
  const workspaceOverviewTrigger = useRef<HTMLButtonElement>(null);
  function closeWorkspaceIntroduction() {
    try {
      if (hideWorkspaceIntroduction) localStorage.setItem(workspaceIntroductionKey, "true");
      else localStorage.removeItem(workspaceIntroductionKey);
    } catch { /* Preference is optional. */ }
    setWorkspaceIntroduction(false);
    setWorkspaceIntroductionSeen(true);
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "auto" });
      workspaceOverviewTrigger.current?.focus({ preventScroll: true });
    });
  }
  const [professionalCenterMounted, setProfessionalCenterMounted] = useState(false);
  const professionalEntry = useRef<HTMLButtonElement>(null);
  const homeScrollPosition = useRef(0);
  const [startedTasks, setStartedTasks] = useState<Set<string>>(() => new Set());
  function markTaskStarted(task: string) {
    setStartedTasks((current) => current.has(task) ? current : new Set(current).add(task));
  }
  const [visitedTasks, setVisitedTasks] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (activeTask) setVisitedTasks(current => new Set(current).add(["profile_assistant", "profile_center"].includes(activeTask) ? "professional_profile" : activeTask));
  }, [activeTask]);
  const [reviewedTasks, setReviewedTasks] = useState<Set<string>>(() => new Set());
  const [editingReviewGroup, setEditingReviewGroup] = useState(false);
  useEffect(() => { setEditingReviewGroup(false); }, [activeTask]);

  function saveFailureMessage(task: string) {
    const es = locale === "es";
    switch (task) {
      case "passport": return es ? "No se pudo guardar el pasaporte." : "The passport could not be saved.";
      case "engagement": return es ? "No se pudo guardar su trabajo en esta producción." : "Your work on this production could not be saved.";
      case "professional_profile": return es ? "No se pudo guardar el perfil profesional." : "The professional profile could not be saved.";
      case "contact_details": return es ? "No se pudieron guardar los datos personales." : "Personal details could not be saved.";
      case "immigration_history": return es ? "No se pudo guardar el historial migratorio." : "Immigration history could not be saved.";
      case "accompanying_family": return es ? "No se pudo guardar la familia acompañante." : "Accompanying family could not be saved.";
      case "legal_history": return es ? "No se pudo guardar el historial legal." : "Legal history could not be saved.";
      case "online_profiles": return es ? "No se pudieron guardar las redes sociales." : "Social media could not be saved.";
      case "other_travel_documents": return es ? "No se pudieron guardar los documentos de viaje." : "Travel documents could not be saved.";
      default: return es ? "No se pudieron guardar los cambios." : "Your changes could not be saved.";
    }
  }

  /** Persists one section to the participant profile API. Resolves to false when the save failed. */
  async function persistTask(task: string, currentAnswers: Answers = answersRef.current, pageIds?: string[]): Promise<boolean> {
    setSavingProfile(task);
    try {
      switch (task) {
        case "passport":
          await mvpApi.saveUserProfilePassport(passportDetailsPayload(currentAnswers, passportFileName, passportStorageKey));
          break;
        case "engagement":
          for (const body of productionWorkPayloads(pageIds ?? workGroups.flat(), currentAnswers)) {
            await mvpApi.saveUserProfileProductionWork(body);
          }
          break;
        case "professional_profile":
          await mvpApi.saveUserProfileProfessionalProfile(professionalProfilePayload(professionalProfileGroups.flat(), currentAnswers));
          break;
        case "contact_details":
          await mvpApi.saveUserProfilePersonalDetails(personalDetailsPayload(currentAnswers));
          await mvpApi.saveUserProfileImmigrationHistory(immigrationHistoryPayload(["pf.identity.other_names"], currentAnswers));
          break;
        case "immigration_history": {
          const sourceIds = pageIds ?? immigrationHistoryGroups.flatMap((group) => group.ids);
          const visibleIds = sourceIds.filter((id) => {
            const concept = allConcepts.find((item) => item.id === id);
            return !concept || applicability(concept, currentAnswers, allConcepts) === true;
          });
          for (const body of immigrationHistoryPayloads(visibleIds, currentAnswers)) {
            await mvpApi.saveUserProfileImmigrationHistory(body);
          }
          break;
        }
        case "accompanying_family":
          await mvpApi.saveUserProfileAccompanyingFamily(accompanyingFamilyPayload(currentAnswers));
          break;
        case "legal_history":
          await mvpApi.saveUserProfileLegalHistory(legalHistoryPayload(currentAnswers));
          break;
        default:
          break;
      }
      return true;
    } catch (err: unknown) {
      const detail = formatApiDetail((err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail);
      setNotice({ kind: "info", message: detail || saveFailureMessage(task) });
      return false;
    } finally {
      setSavingProfile(null);
    }
  }

  /**
   * Stores the passport status / renewal / replacement answers and keeps the local
   * state in sync. The status stays on screen even if the save fails; the notice
   * tells the participant to retry.
   */
  async function persistPassportStatus(value: PassportAvailability): Promise<void> {
    setPassportAvailability(value);
    try {
      await mvpApi.saveUserProfilePassportStatus(passportStatusPayload(value));
    } catch {
      setNotice({ kind: "info", message: locale === "es" ? "No se pudo guardar el estado del pasaporte." : "The passport status could not be saved." });
    }
  }

  async function finishQuestionnaire(task: string) {
    const groups = task === "passport" ? passportGroups : task === "engagement" ? workGroups : task === "professional_profile" ? professionalProfileGroups : task === "contact_details" ? personalGroups.map(group => group.ids) : immigrationHistoryGroups.map(group => group.ids);
    const valid = groups.flat().every(id => {
      const concept = allConcepts.find(c => c.id === id);
      return !concept || !answerError(concept, answers, allConcepts);
    });
    // Let pending answer updates (e.g. repeater saves) settle before reading answersRef.
    await new Promise<void>((resolve) => { requestAnimationFrame(() => resolve()); });
    if (!(await persistTask(task))) return;
    setCompletedTasks(current => { const next = new Set(current); if (valid) next.add(task); else next.delete(task); return next; });
    setReviewedTasks(current => new Set(current).add(task));
    setEditingReviewGroup(false);
    if (task === "contact_details") {
      setPersonalTab("contact");
      setActiveTask(null);
      return;
    }
    document.querySelector(".task-screen-body")?.scrollTo({top: 0});
  }

  const [otherNamesSaveSignal, setOtherNamesSaveSignal] = useState(0);
  const [professionalProfileStep, setProfessionalProfileStep] = useState(0);
  const [profileAssistantStep, setProfileAssistantStep] = useState(5);
  const [profileSourceStep, setProfileSourceStep] = useState(0);
  function changeProfileAssistantStep(step: number) {
    if ([0, 1, 2].includes(step)) setProfileSourceStep(step);
    setProfileAssistantStep(step);
  }
  function previousProfileStep() {
    if (hasProfessionalProfile && profileAssistantStep === 4) setActiveTask(null);
    else setProfileAssistantStep(profileAssistantStep === 4 ? profileSourceStep : 5);
  }
  const [profileSourceText, setProfileSourceText] = useState("");
  const [profileSourceFiles, setProfileSourceFiles] = useState<ResumeFile[]>([]);
  const [uploadingResumes, setUploadingResumes] = useState<string[]>([]);
  const [fetchingXAccount, setFetchingXAccount] = useState(false);
  const [generatingProfile, setGeneratingProfile] = useState(false);
  const [selectedProfileSources, setSelectedProfileSources] = useState<string[]>([]);
  const [profileDraft, setProfileDraft] = useState("");
  const [passportStep, setPassportStep] = useState(0);
  const showingReview = Boolean(activeTask && activeTask !== "contact_details" && reviewedTasks.has(activeTask) && !editingReviewGroup && (activeTask !== "passport" || passportStep > 0));
  const [passportErrors, setPassportErrors] = useState<string[]>([]);
  const [immigrationHistoryStep, setImmigrationHistoryStep] = useState(0);
  const [immigrationHistoryErrors, setImmigrationHistoryErrors] = useState<string[]>([]);
  // Store navigation only, never participant answers, in browser history.
  const navigationKey = JSON.stringify({activeTask, personalTab, workStep, passportStep, passportAvailabilityStep, showPassportAvailability, passportExpiringFlow, professionalProfileStep, profileAssistantStep, profileSourceStep, immigrationHistoryStep, editingReviewGroup, reviewedTasks: [...reviewedTasks]});
  const previousNavigation = useRef<string | null>(null);
  const restoringNavigation = useRef(false);
  useEffect(() => {
    if (restoringNavigation.current) { restoringNavigation.current = false; previousNavigation.current = navigationKey; return; }
    if (previousNavigation.current === navigationKey) return;
    const state = {...window.history.state, gmParticipantNavigation: navigationKey};
    if (previousNavigation.current === null) window.history.replaceState(state, "");
    else window.history.pushState(state, "");
    previousNavigation.current = navigationKey;
  }, [navigationKey]);
  useEffect(() => {
    function restore(event: PopStateEvent) {
      const saved = event.state?.gmParticipantNavigation;
      if (typeof saved !== "string") return;
      const nav = JSON.parse(saved);
      restoringNavigation.current = true;
      setActiveTask(nav.activeTask);
      setPersonalTab(nav.personalTab);
      setWorkStep(nav.workStep);
      setPassportStep(nav.passportStep);
      setShowPassportAvailability(Boolean(nav.showPassportAvailability));
      setPassportAvailabilityStep(nav.passportAvailabilityStep ?? 0);
      setPassportExpiringFlow(Boolean(nav.passportExpiringFlow));
      if (typeof nav.professionalProfileStep === "number") setProfessionalProfileStep(nav.professionalProfileStep);
      if (typeof nav.profileAssistantStep === "number") setProfileAssistantStep(nav.profileAssistantStep);
      if (typeof nav.profileSourceStep === "number") setProfileSourceStep(nav.profileSourceStep);
      setImmigrationHistoryStep(nav.immigrationHistoryStep);
      setEditingReviewGroup(nav.editingReviewGroup);
      setReviewedTasks(new Set(nav.reviewedTasks));
      setFormErrors({}); setPassportErrors([]); setImmigrationHistoryErrors([]);
    }
    window.addEventListener("popstate", restore);
    return () => window.removeEventListener("popstate", restore);
  }, []);
  const professionalCreditCount = entries(answers["pf.career.credits"]).length;
  const savedBiography = savedProfileBiography(answers, insertedDemoAnswers.current);
  function openProfessionalProfile() {
    homeScrollPosition.current = window.scrollY;
    setFormErrors({});
    setReturnToProfessionalProfile(false);
    if (savedBiography) setProfileDraft(savedBiography);
    setProfessionalCenterMounted(true);
    setActiveTask("profile_center");
  }
  useEffect(() => {
    if (activeTask !== "profile_center") return;
    window.scrollTo(0, 0);
    document.querySelector<HTMLElement>(".profile-center-host #pc-introduction-title, .profile-center-host #pc-step-heading")?.focus({ preventScroll: true });
    return () => {
      window.scrollTo(0, homeScrollPosition.current);
      professionalEntry.current?.focus({ preventScroll: true });
    };
  }, [activeTask]);
  const [socialProfiles, setSocialProfiles] = useState<SocialProfile[]>([]);
  const [returnToProfessionalProfile, setReturnToProfessionalProfile] = useState(false);
  const familyMemberCount = entries(answers["pf.family.members"]).length;
  const [familyDraft] = useFormDraft<FamilyMember>("family-draft", {givenNames:"", surnames:"", birthDate:"", passportCountry:"", relationship:""});
  const familyHasDraft = Boolean(familyDraft.legacyFullName || familyDraft.givenNames || familyDraft.surnames || familyDraft.birthDate || familyDraft.relationship);
  const familyReady = familyInformationReady(scalar(answers["pf.family.accompanying"]), familyMemberCount, familyHasDraft);
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(() => new Set());
  const hasProfessionalProfile = Boolean(savedBiography);
  const [menuOpen, setMenuOpen] = useState(false);
  const profileWrapRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    function dismissOutside(event: PointerEvent) {
      if (event.target instanceof Node && !profileWrapRef.current?.contains(event.target)) setMenuOpen(false);
    }
    function dismissEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setMenuOpen(false);
        profileButtonRef.current?.focus();
      }
    }
    document.addEventListener("pointerdown", dismissOutside);
    document.addEventListener("keydown", dismissEscape);
    return () => {
      document.removeEventListener("pointerdown", dismissOutside);
      document.removeEventListener("keydown", dismissEscape);
    };
  }, [menuOpen]);
  const [language, setLanguage] = useState("EN");
  const [invitationStage, setInvitationStage] = useState<"active" | "accepted" | "inactive" | "signin" | null>(null);
  const locale: ProductLocale = language === "ES" ? "es" : "en";
  const socialProfileCount = socialProfiles.length;
  const participantGivenName = scalar(answers["pf.identity.given_names"]).trim() || account.firstName;
  const participantFamilyName = scalar(answers["pf.identity.family_primary"]).trim();
  const participantDisplayName = [participantGivenName, participantFamilyName].filter(Boolean).join(" ") || account.fullName;
  const participantInitials = [participantGivenName, participantFamilyName].filter(Boolean).map(name => Array.from(name)[0]).join("").toUpperCase() || account.initials || "?";
  const participantEmail = scalar(answers["pf.contact.email"]) || account.email;
  const hour = new Date().getHours();
  const dayPart = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
  const greetingPhrase = locale === "es"
    ? { morning: "Buenos días", afternoon: "Buenas tardes", evening: "Buenas noches" }[dayPart]
    : { morning: "Good morning", afternoon: "Good afternoon", evening: "Good evening" }[dayPart];
  const copy = locale === "es" ? {
    documents: "Documentos", editProfile: "Editar perfil", closeMenu: "Cerrar menú",
    greeting: participantGivenName ? `${greetingPhrase}, ${participantGivenName}.` : `${greetingPhrase}.`, actionNeeded: "Acción necesaria",
    privacy: "Privacidad", dataUse: "Cómo usamos sus datos",
    profileInformation: "Perfil", travelDocuments: "Documentos de viaje", immigration: "Inmigración", production: "Producción", documentCenter: "Centro de documentos",
  } : {
    documents: "Documents", editProfile: "Edit profile", closeMenu: "Close menu",
    greeting: participantGivenName ? `${greetingPhrase}, ${participantGivenName}.` : `${greetingPhrase}.`, actionNeeded: "Action needed",
    privacy: "Privacy", dataUse: "How we use your data",
    profileInformation: "Profile", travelDocuments: "Travel documents", immigration: "Immigration", production: "Production", documentCenter: "Document Center",
  };
  const passportRequest = experience.documents.find((request) => request.id.endsWith(".passport"));
  const visibleDocuments = documentCenterRequests(experience.documents) as DocumentRequest[];
  const hasDocumentCenterRequests = visibleDocuments.some(document => applicability(document, answers, allConcepts) === true || (caseDocumentRequests[document.id]?.status === "requested" && caseDocumentRequests[document.id]?.instructions.en.trim()) || documentFiles[document.id] || documentResponses[document.id]);
  const documentCenterComplete = hasDocumentCenterRequests && !visibleDocuments.some(d => Boolean(documentResponses[d.id])) && Object.keys(caseDocumentRequests).filter(id => visibleDocuments.some(d => d.id === id)).every(id => Boolean(documentFiles[id])) && documentCompletion(visibleDocuments, documentFiles, answers, allConcepts);

  function setAnswer(id: string, value: AnswerValue) {
    if (["pf.passport.issuing_country", "pf.identity.given_names", "pf.identity.family_primary", "pf.location.current_country", "pf.location.country_of_legal_residence"].includes(id)) editedDefaultFields.current.add(id);
    if (id === "pf.identity.nationalities") onFactsChange({passport_country: scalar(value)});
    if (id === "pf.engagement.engagement_type" && experience.destination === "IT") onFactsChange({employment_relationship: value});
    if (id === "pf.engagement.dates_in_country" && experience.destination === "ES") {
      const dates = record(value);
      if (dates.start && dates.end && dates.end >= dates.start) {
        const days = Math.round((Date.parse(dates.end) - Date.parse(dates.start)) / 86400000) + 1;
        if (Number.isFinite(days)) onFactsChange({stay_days: days});
      }
    }
    setAnswers((current) => ({...current, [id]: value,
      ...(id === "pf.location.country_of_legal_residence" && scalar(current[id]) !== scalar(value)
        ? {"pf.location.apply_outside_residence": "", "pf.location.expected_visa_application_country": ""} : {}),
      ...(id === "pf.location.apply_outside_residence" ? {"pf.location.expected_visa_application_country": ""} : {}),
      ...(id === "pf.family.accompanying" && value === "no" ? {"pf.family.members": []} : {}),
      ...(id === "pf.es.nie_held" && value !== "yes" ? {"pf.es.nie_number": ""} : {}),
      ...(id === "pf.engagement.paid" && value !== "yes" ? {"pf.engagement.compensation": { amount: "", currency: "" }} : {}),
    }));
    setFormErrors((current) => { const next = {...current}; delete next[id]; if (["pf.location.apply_outside_residence", "pf.location.country_of_legal_residence"].includes(id)) delete next["pf.location.expected_visa_application_country"]; return next; });
    setCompletedTasks((current) => {
      const next = new Set(current);
      const concept = allConcepts.find((item) => item.id === id);
      if (concept?.section) next.delete(concept.section);
      if (id.startsWith("pf.identity.") || id.startsWith("pf.passport.")) next.delete("passport");
      if (id === "pf.identity.other_names") next.delete("contact_details");
      if (id === "pf.location.expected_visa_application_country" || id === "pf.location.apply_outside_residence") next.delete("immigration_history");
      return next;
    });
  }
  useEffect(() => {
    setCompletedTasks(new Set());
    setReviewedTasks(new Set());
    setVisitedTasks(new Set());
    setPersonalTab("contact");
    // Refresh routing without sending an open passport form back to upload.
    setPassportStep(current => activeTask === "passport" ? Math.min(current, experience.pages.passport.length) : 0);
    setEditingReviewGroup(false);
    setWorkStep(0);
    setProfessionalProfileStep(0);
    setImmigrationHistoryStep(0);
    setActiveTask(current => current === "passport" ? current : null);
    setFormErrors({});
  }, [experience.id]);
  useEffect(() => {
    const previous = previousSupplied.current;
    setAnswers((current) => {
      const next = mergeSuppliedAnswers(current, supplied, previous, editedDefaultFields.current);
      for (const [id, value] of Object.entries(accountFieldSeeds(account))) {
        if (!scalar(next[id]).trim()) next[id] = value;
      }
      return next;
    });
    previousSupplied.current = supplied;
  }, [experience.id, suppliedKey, account]);
  // Load saved sections from the participant profile API; local edits are never overridden.
  useEffect(() => {
    let cancelled = false;
    const ignore = () => undefined;
    mvpApi.getUserProfilePersonalDetails()
      .then((res) => {
        if (cancelled) return;
        const saved = answersFromPersonalDetails(res?.data);
        if (!Object.keys(saved).length) return;
        setAnswers((current) => ({ ...current, ...saved }));
        setReviewedTasks((current) => new Set(current).add("contact_details"));
        setCompletedTasks((current) => new Set(current).add("contact_details"));
      })
      .catch(ignore);
    mvpApi.getUserProfileSocialMedia()
      .then((res) => {
        if (cancelled) return;
        const profiles = socialProfilesFromApi(res?.data);
        if (!profiles.length) return;
        setSocialProfiles((current) => current.length ? current : profiles);
        setCompletedTasks((current) => new Set(current).add("online_profiles"));
      })
      .catch(ignore);
    mvpApi.getUserProfileTravelDocuments()
      .then((res) => {
        if (cancelled) return;
        const documents = travelDocumentsFromApi(res?.data);
        if (!documents.length) return;
        setAnswers((current) => entries(current["pf.passport.other_details"]).length ? current : { ...current, "pf.passport.other_details": documents });
      })
      .catch(ignore);
    const sectionLoads: [string, () => Promise<{ data?: unknown }>, (data: unknown) => Answers][] = [
      ["engagement", () => mvpApi.getUserProfileProductionWork(), (data) => answersFromProductionWork(data as Parameters<typeof answersFromProductionWork>[0])],
      ["accompanying_family", () => mvpApi.getUserProfileAccompanyingFamily(), (data) => answersFromAccompanyingFamily(data as Parameters<typeof answersFromAccompanyingFamily>[0])],
      ["immigration_history", () => mvpApi.getUserProfileImmigrationHistory(), (data) => answersFromImmigrationHistory(data as Parameters<typeof answersFromImmigrationHistory>[0])],
      ["legal_history", () => mvpApi.getUserProfileLegalHistory(), (data) => answersFromLegalHistory(data as Parameters<typeof answersFromLegalHistory>[0])],
      ["professional_profile", () => mvpApi.getUserProfileProfessionalProfile(), (data) => answersFromProfessionalProfile(data as Parameters<typeof answersFromProfessionalProfile>[0])],
    ];
    for (const [task, load, toAnswers] of sectionLoads) {
      load()
        .then((res) => {
          if (cancelled) return;
          const saved = toAnswers(res?.data);
          if (!Object.keys(saved).length) return;
          setAnswers((current) => fillEmptyAnswers(current, saved));
          setReviewedTasks((current) => new Set(current).add(task));
          setCompletedTasks((current) => new Set(current).add(task));
        })
        .catch(ignore);
    }
    mvpApi.getUserProfilePassport()
      .then((res) => {
        if (cancelled) return;
        const saved = answersFromSavedPassport(res?.data);
        if (Object.keys(saved).length) {
          setAnswers((current) => fillEmptyAnswers(current, saved));
          setReviewedTasks((current) => new Set(current).add("passport"));
          setCompletedTasks((current) => new Set(current).add("passport"));
        }
        if (res?.data?.file_name) {
          setPassportFileName((current) => current || res.data.file_name);
          setPassportVerifiedPreview(true);
        }
        if (res?.data?.storage_key) setPassportStorageKey((current) => current || res.data.storage_key);
      })
      .catch(ignore);
    mvpApi.getUserProfilePassportStatus()
      .then((res) => {
        if (cancelled) return;
        const saved = passportStatusFromSaved(res?.data);
        if (saved) setPassportAvailability((current) => (current.reason ? current : saved));
      })
      .catch(ignore);
    mvpApi.getUserProfileResumes()
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data?.resumes) ? res.data.resumes : [];
        const stored: ResumeFile[] = rows
          .filter((row: { resume_id?: string; file_name?: string }) => row?.resume_id)
          .map((row: { resume_id: string; file_name?: string }) => ({ id: String(row.resume_id), name: String(row.file_name || "") }));
        if (stored.length) setProfileSourceFiles((current) => (current.length ? current : stored));
      })
      .catch(ignore);
    mvpApi.getUserProfileExperienceText()
      .then((res) => {
        if (cancelled) return;
        const text = String(res?.data?.text || "");
        if (text) setProfileSourceText((current) => current || text);
      })
      .catch(ignore);
    mvpApi.getUserProfileDocuments()
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data?.documents) ? res.data.documents : [];
        if (!rows.length) return;
        setDocumentFiles((current) => {
          const next = { ...current };
          for (const row of rows) {
            if (row?.document_id && row?.file_name && !next[row.document_id]) next[row.document_id] = row.file_name;
          }
          return next;
        });
      })
      .catch(ignore);
    return () => { cancelled = true; };
  }, []);
  function validateFields(ids: string[]) {
    const errors = Object.fromEntries(ids.map((id) => {
      const concept = allConcepts.find((item) => item.id === id);
      return [id, concept ? answerError(concept, answers, allConcepts) : null];
    }).filter((entry): entry is [string, string] => Boolean(entry[1])));
    setFormErrors(errors);
    if (Object.keys(errors).length) {
      window.setTimeout(() => document.getElementById(`question-${Object.keys(errors)[0]}`)?.scrollIntoView({block: "center", behavior: "smooth"}), 0);
      return false;
    }
    return true;
  }

  function updateSocialProfiles(profiles: SocialProfile[]) {
    setSocialProfiles(profiles);
    setAnswer("pf.social.accounts", profiles.map((profile) => ({platform: profile.platform, platform_other: profile.platformOther, handle: profile.handle, profile_url: profile.profileUrl, account_kind: profile.accountKind})));
    setCompletedTasks((current) => {
      const next = new Set(current);
      if (profiles.length) next.add("online_profiles");
      else next.delete("online_profiles");
      return next;
    });
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invitationId = params.get("invite");
    // Design fixtures only. Production resolves tokens and authorization on the server.
    if (params.get("signin") === "1") setInvitationStage("signin");
    else if (invitationId) setInvitationStage(invitationId === invitationFlow.sample.invitation_id ? "active" : "inactive");
  }, []);
  const conceptsForSection = (sectionKey: string) => {
    return allConcepts.filter((concept) => concept.section === sectionKey);
  };
  const sectionHasContent = (sectionKey: string) =>
    sectionKey === "passport"
    || sectionKey === "documents"
    || conceptsForSection(sectionKey).length > 0;
  const personalTasks = experience.navigation.profile.filter((key) => key !== "professional_profile" && key !== "online_profiles" && sectionHasContent(key));
  const travelDocumentTasks = experience.navigation.travel.filter((key) => key !== "passport" && key !== "other_travel_documents" && sectionHasContent(key));
  const immigrationTasks = experience.navigation.immigration.filter(sectionHasContent);
  const taskTitle = (taskKey: string) => taskKey === "documents"
    ? copy.documentCenter
    : taskKey === "passport" ? (locale === "es" ? "Datos del pasaporte" : "Passport details")
    : taskKey === "other_travel_documents" ? (locale === "es" ? "Documentos de viaje" : "Travel documents")
    : taskKey === "contact_details" ? (locale === "es" ? "Datos personales" : "Personal details")
    : taskKey === "online_profiles" ? (locale === "es" ? "Redes sociales" : "Social media")
    : taskKey === "immigration_history" ? (locale === "es" ? "Datos migratorios" : "Immigration details")
    : taskKey === "accompanying_family" ? (locale === "es" ? "Familiares acompañantes" : "Accompanying family members")
    : taskKey === "engagement" ? (locale === "es" ? "Su trabajo en esta producción" : "Your work on this production")
    : localized(section(taskKey)?.title, locale);
  // General intake asks residence and intended application country.
  // Physical-presence routing remains case context, not inferred from either answer.
  const immigrationHistoryGroups = experience.pages.immigration_history
    .map((group) => ({...group,
      ids: group.ids.filter(id => id !== "pf.location.current_country"),
      title: group.ids.includes("pf.location.country_of_legal_residence") ? (locale === "es" ? "Residencia y solicitud de visa" : "Residence and visa application") : localized(group.title, locale),
    }))
    .filter(group => group.ids.some(id => { const c = allConcepts.find(c => c.id === id); return c && applicability(c, answers, allConcepts) !== false; }));
  const legalHistoryIds = ["pf.legal.arrested", "pf.legal.charged", "pf.legal.convicted"];
  const legalHistoryComplete = legalHistoryIds.every((conceptId) => scalar(answers[conceptId]).trim());
  const activeTaskTitle = showingReview && activeTask ? taskTitle(activeTask) : activeTask === "professional_profile"
      ? (locale === "es" ? "Perfil profesional" : "Professional profile")
      : activeTask === "profile_assistant"
        ? (hasProfessionalProfile ? (locale === "es" ? "Editar su perfil" : "Edit your profile") : (locale === "es" ? "Crear su perfil" : "Create your profile"))
      : activeTask === "passport" ? taskTitle("passport")
      : activeTask === "immigration_history"
        ? taskTitle("immigration_history")
      : activeTask === "legal_history"
        ? (locale === "es" ? "Antecedentes legales" : "Legal history")
      : activeTask === "engagement" ? taskTitle("engagement")
      : activeTask ? taskTitle(activeTask) : "";
  const activeConcepts = activeTask ? conceptsForSection(activeTask) : [];
  const professionalProfileGroups = experience.pages.professional_profile.map((group) => group.ids);
  const workGroups = experience.pages.engagement.map((group) => group.ids);
  function continueWork() {
    if (!validateFields(workGroups[workStep])) return;
    if (editingReviewGroup) { finishQuestionnaire("engagement"); return; }
    if (workStep < workGroups.length - 1) {
      void persistTask("engagement", answersRef.current, workGroups[workStep]);
      setWorkStep((step) => step + 1);
    }
    else { finishQuestionnaire("engagement"); }
  }
  const [otherNameDraft, setOtherNameDraft] = useFormDraft<OtherName>("other-names-draft", {givenName:"", middleName:"", familyName:""});
  const [otherNameAnswer] = useFormDraft<string>("other-names-answer", "");
  const [otherNameEditing, setOtherNameEditing] = useFormDraft<number | null>("other-names-edit", null);
  function finishContact() {
    if (!validateFields(personalGroups[personalStep].ids)) return;
    if (personalStep < personalGroups.length - 1) {
      setPersonalTab(personalGroups[personalStep + 1].key);
      return;
    }
    if ((scalar(answers["pf.identity.other_names_used"]) === "yes" || otherNameAnswer === "yes") && Object.values(otherNameDraft).some(value => value.trim())) {
      const saved = entries(answers["pf.identity.other_names"]);
      const entry = {given_name:otherNameDraft.givenName, middle_name:otherNameDraft.middleName, family_name:otherNameDraft.familyName};
      setAnswer("pf.identity.other_names_used", "yes");
      setAnswer("pf.identity.other_names", otherNameEditing === null ? [...saved, entry] : saved.map((name,index) => index === otherNameEditing ? entry : name));
      setOtherNameDraft({givenName:"", middleName:"", familyName:""});
      setOtherNameEditing(null);
    }
    finishQuestionnaire("contact_details");
  }
  const [travelDocumentOrigin, setTravelDocumentOrigin] = useState<"documents" | "passport">("documents");
  const [, setTravelDocumentDraft] = useFormDraft<Record<string, string>>("pf.passport.other_details-draft", {});
  function openExceptionalTravelDocuments(origin: "documents" | "passport") {
    setTravelDocumentOrigin(origin);
    if (origin === "documents") setTravelDocumentDraft(current => ({...current, document_kind: "passport"}));
    setEditingReviewGroup(false);
    setActiveTask("other_travel_documents");
  }

  const passportIdentityIds = [
    "pf.identity.given_names",
    "pf.identity.middle_names",
    "pf.identity.family_primary",
    "pf.identity.family_second",
    "pf.identity.birth_date",
    "pf.identity.birth_city",
    "pf.identity.birth_country",
    "pf.identity.nationalities",
  ];
  const passportReviewConcepts = [
    ...passportIdentityIds
      .map((id) => allConcepts.find((concept) => concept.id === id))
      .filter((concept): concept is ContractConcept => Boolean(concept)),
    ...conceptsForSection("passport"),
  ];
  // Present the holder first; retain the KB field identities and requirements.
  const passportPages = passportLayout(experience.pages.passport);
  const passportGroups = [[], ...passportPages.map((group) => group.ids)];

  async function continueProfessionalProfile() {
    const checkingIds = professionalProfileStep === professionalProfileGroups.length - 1 ? professionalProfileGroups.flat() : professionalProfileGroups[professionalProfileStep];
    if (!validateFields(checkingIds)) {
      const firstInvalid = professionalProfileGroups.findIndex((group) => group.some((id) => {
        const concept = allConcepts.find((item) => item.id === id);
        return concept && answerError(concept, answers, allConcepts);
      }));
      if (firstInvalid >= 0) setProfessionalProfileStep(firstInvalid);
      return;
    }
    if (!(await persistTask("professional_profile"))) return;
    if (!editingReviewGroup && professionalProfileStep < professionalProfileGroups.length - 1) {
      setProfessionalProfileStep((step) => step + 1);
      return;
    }
    const requiredCareerFieldsComplete = professionalProfileGroups[0].every((conceptId) => scalar(answers[conceptId]).trim());
    if (requiredCareerFieldsComplete && professionalCreditCount > 0) {
      setCompletedTasks((current) => new Set(current).add("professional_profile"));
    }
    setReviewedTasks(current => new Set(current).add("professional_profile"));
    setEditingReviewGroup(false);
  }

  const [socialDraft, setSocialDraft] = useFormDraft<SocialProfile>("social-draft", emptySocialProfile);
  const [socialEdit, setSocialEdit] = useFormDraft<number | null>("social-edit", null);
  const [, setSocialErrors] = useFormDraft<boolean>("social-errors", false);
  const [socialExit, setSocialExit] = useState<"done" | "close" | null>(null);
  const socialExitDialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (socialExit) { socialExitDialog.current?.showModal(); socialExitDialog.current?.querySelector<HTMLElement>("button:last-child")?.focus(); }
    else socialExitDialog.current?.close();
  }, [socialExit]);
  function requestSocialExit(reason: "done" | "close") {
    const original = socialEdit === null ? emptySocialProfile : socialProfiles[socialEdit] ?? emptySocialProfile;
    const dirty = (Object.keys(emptySocialProfile) as (keyof SocialProfile)[]).some((key) => socialDraft[key] !== original[key]);
    if (dirty) setSocialExit(reason);
    else completeSocialExit(reason);
  }
  async function completeSocialExit(reason: "done" | "close", accounts: SocialProfile[] = socialProfiles) {
    if (reason === "done") {
      setSavingProfile("online_profiles");
      try {
        await mvpApi.saveUserProfileSocialMedia(socialMediaPayload(accounts));
      } catch {
        setNotice({ kind: "info", message: saveFailureMessage("online_profiles") });
        return;
      } finally {
        setSavingProfile(null);
      }
    }
    setSocialDraft(emptySocialProfile);
    setSocialEdit(null);
    setSocialErrors(false);
    setSocialExit(null);
    if (reason === "done" && accounts.length > 0) setCompletedTasks((current) => new Set(current).add("online_profiles"));
    setActiveTask(null);
  }
  function saveSocialAndExit() {
    if (!socialProfileValid(socialDraft)) {
      setSocialExit(null);
      setSocialErrors(true);
      const target = !socialDraft.platform ? "social-platform" : socialDraft.platform === "other" && !socialDraft.platformOther.trim() ? "social-platform-other" : "social-handle";
      window.setTimeout(() => { document.getElementById(target)?.focus(); }, 0);
      return;
    }
    const saved = socialEdit === null ? [...socialProfiles, socialDraft] : socialProfiles.map((profile, index) => index === socialEdit ? socialDraft : profile);
    setSocialProfiles(saved);
    void completeSocialExit(socialExit ?? "close", saved);
  }

  function finishOnlineProfiles() { requestSocialExit("done"); }

  async function generateProfessionalProfileDraft() {
    if (generatingProfile) return;
    if (profileAssistantStep === 1) void persistExperienceText(profileSourceText);
    setGeneratingProfile(true);
    setProfileAssistantStep(4);
    try {
      const { data } = await mvpApi.synthesizeProfessionalProfile({
        procedureSlug: account.procedureSlug,
        notes: profileSourceText,
        links: selectedProfileSources,
        existingFileNames: profileSourceFiles.map((file) => file.name),
      });
      const generated = answersFromSynthesizedProfile(data);
      const biography = scalar(generated["pf.career.biography"]).trim();
      setProfileDraft(biography);
      setAnswers((current) => {
        const next = fillEmptyAnswers(current, generated);
        if (biography) next["pf.career.biography"] = biography;
        return next;
      });
      const warnings = Array.isArray(data?.warnings) ? data.warnings.filter(Boolean) : [];
      if (!biography) {
        setNotice({
          kind: "info",
          message: locale === "es"
            ? "No se pudo crear un perfil a partir de esa fuente. Añada más detalle o pruebe otra fuente."
            : "A profile could not be created from that source. Add more detail or try another source.",
        });
      } else if (warnings.length) {
        setNotice({ kind: "info", message: String(warnings[0]) });
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      setNotice({
        kind: "info",
        message: typeof detail === "string" && detail
          ? detail
          : locale === "es" ? "No se pudo crear su perfil. Inténtelo de nuevo." : "Your profile could not be created. Please try again.",
      });
    } finally {
      setGeneratingProfile(false);
    }
  }

  async function persistExperienceText(text: string) {
    try {
      await mvpApi.saveUserProfileExperienceText(text);
    } catch {
      setNotice({ kind: "info", message: locale === "es" ? "No se pudo guardar su experiencia profesional." : "Your professional experience could not be saved." });
    }
  }

  /**
   * Pulls the X account's public profile and posts through the backend (stored as
   * raw JSON + text in Mongo), then opens the draft review. If the account cannot be
   * read at all the user stays on the step with an explanation.
   */
  async function useXAccountForProfile(handle: string) {
    if (fetchingXAccount) return;
    setFetchingXAccount(true);
    try {
      await mvpApi.saveUserProfileXAccount(handle);
      await generateProfessionalProfileDraft();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      setNotice({
        kind: "info",
        message: typeof detail === "string" && detail
          ? detail
          : locale === "es" ? "No se pudo leer esa cuenta de X. Inténtelo de nuevo." : "That X account could not be read. Please try again.",
      });
    } finally {
      setFetchingXAccount(false);
    }
  }

  /**
   * Uploads each chosen resume (S3 binary + Mongo record with extracted text),
   * then opens the draft review once at least one file was stored.
   */
  async function uploadResumeFiles(files: File[]) {
    const names = files.map(file => file.name);
    setUploadingResumes(current => [...current, ...names]);
    const stored: ResumeFile[] = [];
    const failed: string[] = [];
    for (const file of files) {
      try {
        const res = await mvpApi.uploadUserProfileResume(file);
        const data = res?.data || {};
        if (data.resume_id) stored.push({ id: String(data.resume_id), name: String(data.file_name || file.name) });
        else failed.push(file.name);
      } catch {
        failed.push(file.name);
      } finally {
        setUploadingResumes(current => {
          const next = [...current];
          next.splice(next.indexOf(file.name), 1);
          return next;
        });
      }
    }
    if (stored.length) setProfileSourceFiles(current => [...current, ...stored]);
    if (failed.length) {
      setNotice({
        kind: "info",
        message: locale === "es"
          ? `No se pudo subir: ${failed.join(", ")}. Elija un PDF, un documento de Word o un archivo de texto de hasta 6 MB.`
          : `Could not upload: ${failed.join(", ")}. Choose a PDF, Word document, or text file up to 6MB.`,
      });
    }
    if (stored.length) await generateProfessionalProfileDraft();
  }

  async function removeResumeFile(file: ResumeFile) {
    setProfileSourceFiles(current => current.filter(item => item.id !== file.id));
    try {
      await mvpApi.deleteUserProfileResume(file.id);
    } catch {
      setProfileSourceFiles(current => (current.some(item => item.id === file.id) ? current : [...current, file]));
      setNotice({ kind: "info", message: locale === "es" ? "No se pudo eliminar el archivo." : "The file could not be removed." });
    }
  }

  async function saveProfessionalProfileDraft() {
    if (!profileDraft.trim()) return;
    delete insertedDemoAnswers.current["pf.career.biography"];
    const next = { ...answersRef.current, "pf.career.biography": profileDraft };
    setAnswers(next);
    setSavingProfile("professional_profile");
    try {
      await mvpApi.saveUserProfileProfessionalProfile(professionalProfilePayload(["pf.career.biography"], next));
    } catch {
      setNotice({ kind: "info", message: saveFailureMessage("professional_profile") });
      return;
    } finally {
      setSavingProfile(null);
    }
    setProfileAssistantStep(5);
    setActiveTask(returnToProfessionalProfile ? "professional_profile" : null);
    setReturnToProfessionalProfile(false);
  }

  function discardProfessionalProfileDraft() {
    setProfileDraft(savedBiography);
    setProfileAssistantStep(5);
    setActiveTask(returnToProfessionalProfile ? "professional_profile" : null);
    setReturnToProfessionalProfile(false);
  }

  const personalGroups = (["contact", "address", "names"] as const).map(key => ({
    key,
    title: key === "contact" ? (locale === "es" ? "Contacto" : "Contact") : key === "address" ? (locale === "es" ? "Domicilio" : "Address") : (locale === "es" ? "Otros nombres" : "Other names"),
    ids: conceptsForSection("contact_details").filter(c => c.id !== "pf.contact.language").filter(c => key === "names" ? c.id === "pf.identity.other_names" : c.id !== "pf.identity.other_names" && (c.answer.shape === "postal_address") === (key === "address")).map(c => c.id),
  }));
  const personalStep = Math.max(0, personalGroups.findIndex(group => group.key === personalTab));
  const reviewGroups = activeTask === "passport"
    ? [{title: locale === "es" ? "Documento del pasaporte" : "Passport document", ids: [] as string[]}, ...passportPages.map((g) => ({...g, title: !g.ids.includes("pf.identity.given_names") ? (locale === "es" ? "Datos del documento" : "Document information") : (locale === "es" ? "Titular del pasaporte" : "Passport holder")}))]
    : activeTask === "engagement" ? experience.pages.engagement.map(g => ({...g, title: pageTitle(g.title, locale)}))
    : activeTask === "professional_profile" ? experience.pages.professional_profile.map(g => ({...g, title: pageTitle(g.title, locale)}))
    : activeTask === "accompanying_family" || activeTask === "legal_history" ? [{title: taskTitle(activeTask), ids: conceptsForSection(activeTask).map(c => c.id)}]
    : activeTask === "contact_details" ? personalGroups : immigrationHistoryGroups;
  const formProgress = guidedProgress({task: activeTask, review: showingReview, editing: editingReviewGroup,
    passportStep, passportCount: passportGroups.length - 1, passportException: showPassportAvailability || Boolean(passportUploadResult),
    personalStep, personalCount: personalGroups.length, workStep, workCount: workGroups.length,
    immigrationStep: immigrationHistoryStep, immigrationCount: immigrationHistoryGroups.length,
    professionalStep: professionalProfileStep, professionalCount: professionalProfileGroups.length,
    builderStep: profileAssistantStep, savedProfile: hasProfessionalProfile});
  function reviewAnswer(concept: ContractConcept) {
    const value = answers[concept.id];
    if (concept.id === "pf.family.members") {
      const plans = scalar(answers["pf.family.travel_plans"]);
      const planLabels: Record<string, string> = locale === "es" ? {confirmed:"Confirmados", considering:"Por decidir", mixed:"Varían según el familiar"} : {confirmed:"Confirmed", considering:"Still considering", mixed:"Plans differ by family member"};
      return [...entries(value).map(member => [member.full_name || [member.given_names, member.surnames].filter(Boolean).join(" "), member.relationship, member.birth_date].filter(Boolean).join(", ")), ...(planLabels[plans] ? [(locale === "es" ? "Planes de viaje: " : "Family travel plans: ") + planLabels[plans]] : [])].join("; ");
    }
    if (concept.id === "pf.identity.other_names" && !entries(value).length && (scalar(answers["pf.identity.other_names_used"]) === "no" || otherNameAnswer === "no")) return locale === "es" ? "No" : "No";
    const format = (value: unknown, field: ContractConcept | ContractEntryField): string => {
      if (Array.isArray(value)) return value.map(item => format(item, field)).filter(Boolean).join("; ");
      if (value && typeof value === "object") {
        if (field.answer.shape === "boolean_with_explanation") {
          const detail = value as Record<string, unknown>;
          const choice = detail.value === "yes" ? (locale === "es" ? "Sí" : "Yes") : detail.value === "no" ? "No" : "";
          return [choice, detail.value === "yes" ? detail.explanation : ""].filter(Boolean).join(": ");
        }
        if (field.answer.shape === "postal_address") {
          const address = addressDraft(value as AnswerValue);
          const template = addressTemplate(address.country);
          return [...(address.mode === "freeform" || !template ? [address.freeform] : template.fields.map(f => address[f.id])), address.country ? countryDisplayName(address.country, locale) : ""].filter(Boolean).join(", ");
        }
        if (field.answer.shape === "date_interval") {
          const interval = value as Record<string, unknown>;
          return [interval.start, interval.end].filter(Boolean).map(date => new Date(`${date}T12:00:00`).toLocaleDateString(locale === "es" ? "es" : "en-US", {month: "short", day: "numeric", year: "numeric"})).join(" – ");
        }
        return (concept.answer.entry_shape || []).map(entryField => {
          const text = format((value as Record<string, unknown>)[entryField.id], entryField);
          return text ? `${localized(entryField.label, locale)}: ${text}` : "";
        }).filter(Boolean).join(" · ");
      }
      const text = value == null ? "" : String(value);
      if (!text) return "";
      const option = field.answer.options?.find(option => option.value === text);
      if (option) return localized(option.label, locale);
      if (text === "yes") return locale === "es" ? "Sí" : "Yes";
      if (text === "no") return "No";
      if (field.answer.shape === "calendar_date" && /^\d{4}-\d{2}-\d{2}$/.test(text)) return new Date(`${text}T12:00:00`).toLocaleDateString(locale === "es" ? "es" : "en-US", {month: "short", day: "numeric", year: "numeric"});
      return field.answer.shape === "country_reference" ? countryDisplayName(text, locale) : text;
    };
    return format(value, concept) || (locale === "es" ? "Sin respuesta" : "Not provided");
  }

  function continuePassport() {
    if (!validateFields(passportGroups[passportStep])) return;
    // Required checks apply to the identity and passport fields in the KB.
    const missing = passportGroups[passportStep]
      .map((id) => passportReviewConcepts.find((concept) => concept.id === id))
      .filter((concept): concept is ContractConcept => Boolean(concept))
      .filter((concept) => concept.requiredness === "required")
      .filter((concept) => !scalar(answers[concept.id]).trim())
      .map((concept) => concept.id);
    if (missing.length) {
      setPassportErrors(missing);
      document.getElementById(`question-${missing[0]}`)?.focus();
      return;
    }
    setPassportErrors([]);
    const target = passportContinueTarget(passportStep, editingReviewGroup, passportGroups.length, reviewedTasks.has("passport"));
    if (!target.review) {
      setPassportStep(target.step);
      return;
    }
    if (passportStep === 0) setPassportStep(1);
    finishQuestionnaire("passport");
  }

  useEffect(() => {
    if (activeTask !== "passport") return;
    document.querySelector(".task-screen-body")?.scrollTo({ top: 0 });
  }, [activeTask, passportStep]);
  useEffect(() => { document.querySelector(".task-screen-body")?.scrollTo({top: 0}); }, [activeTask, workStep, professionalProfileStep, profileAssistantStep, immigrationHistoryStep, personalTab, showingReview, editingReviewGroup]);

  // Keep modal navigation within the active task and restore its entry point.
  useEffect(() => {
    if (!activeTask) return;
    const dialog = document.querySelector<HTMLElement>(".task-screen");
    if (!dialog) return;
    const trigger = document.activeElement as HTMLElement | null;
    const background = Array.from(dialog.parentElement?.children ?? []).filter((node) => node !== dialog && !node.classList.contains("toast") && !node.classList.contains("social-exit-dialog") && !node.classList.contains("atlas-dialog")) as HTMLElement[];
    const previousOverflow = document.body.style.overflow;
    background.forEach((node) => { node.inert = true; });
    document.body.style.overflow = "hidden";
    dialog.focus({ preventScroll: true });
    function trapFocus(event: KeyboardEvent) {
      if (event.key !== "Tab" || !dialog) return;
      const controls = Array.from(dialog.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [tabindex="0"]')).filter((node) => node.getClientRects().length > 0);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) { event.preventDefault(); first.focus(); }
    }
    dialog.addEventListener("keydown", trapFocus);
    return () => {
      background.forEach((node) => { node.inert = false; });
      document.body.style.overflow = previousOverflow;
      dialog.removeEventListener("keydown", trapFocus);
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [activeTask]);
  useEffect(() => { document.documentElement.lang = locale === "en" ? "en-US" : "es"; }, [locale]);

  async function finishAccompanyingFamily() {
    const accompanying = answers["pf.family.accompanying"];
    if (!(await persistTask("accompanying_family"))) return;
    if (accompanying === "no" || (accompanying === "yes" && familyMemberCount > 0 && !familyHasDraft)) {
      setCompletedTasks((current) => new Set(current).add("accompanying_family"));
    }
    setReviewedTasks(current => new Set(current).add("accompanying_family"));
    setEditingReviewGroup(false);
  }

  function continueImmigrationHistory() {
    const currentGroup = immigrationHistoryGroups[immigrationHistoryStep];
    if (!validateFields(currentGroup.ids)) return;
    if (currentGroup.ids.includes("pf.location.country_of_legal_residence") && scalar(answers["pf.location.apply_outside_residence"]) === "yes") {
      const country = scalar(answers["pf.location.expected_visa_application_country"]);
      if (!country || country === scalar(answers["pf.location.country_of_legal_residence"])) {
        setFormErrors(current => ({...current, "pf.location.expected_visa_application_country": locale === "es" ? "Seleccione el país donde tiene previsto solicitar la visa. Si es su país de residencia, seleccione No." : "Select the country where you plan to apply. If it is your country of residence, select No."}));
        document.getElementById("visa-application-country")?.focus();
        return;
      }
    }

    setImmigrationHistoryErrors([]);
    if (currentGroup.ids.includes("pf.identity.other_names")) {
      setOtherNamesSaveSignal((signal) => signal + 1);
    }
    if (editingReviewGroup) { finishQuestionnaire("immigration_history"); return; }
    if (immigrationHistoryStep < immigrationHistoryGroups.length - 1) {
      void persistTask("immigration_history", answersRef.current, currentGroup.ids);
      setImmigrationHistoryStep((step) => step + 1);
      return;
    }
    finishQuestionnaire("immigration_history");
  }

  async function finishLegalHistory() {
    if (!legalHistoryComplete) return;
    if (!(await persistTask("legal_history"))) return;
    setCompletedTasks((current) => new Set(current).add("legal_history"));
    setReviewedTasks(current => new Set(current).add("legal_history"));
    setEditingReviewGroup(false);
  }

  const backDestination = sectionBackDestination({task: activeTask, passportUploadResult, showPassportAvailability, passportAvailabilityStep, showingReview, editingReviewGroup, passportStep, profileAssistantStep, hasProfessionalProfile, personalStep, workStep, professionalProfileStep, immigrationHistoryStep});
  const backLabel = backDestination === "home" ? (locale === "es" ? "Inicio" : "Home") : (locale === "es" ? "Volver" : "Back");
  const primaryLabel = (task: string, label: string) => savingProfile === task ? (locale === "es" ? "Guardando…" : "Saving…") : label;
  const backAccessibleLabel = backDestination === "passport-center" ? (locale === "es" ? "Volver al centro de pasaportes" : "Back to passport center") : backDestination === "passport-status" ? (locale === "es" ? "Volver al estado del pasaporte" : "Back to passport status") : backDestination === "home" ? (locale === "es" ? "Volver al inicio" : "Back to home") : backLabel;
  const includesFamily = immigrationTasks.includes("accompanying_family") || personalTasks.includes("accompanying_family");
  function areaStatus(tasks: string[]) {
    const completed = new Set(completedTasks);
    if (!familyReady) completed.delete("accompanying_family");
    const state = preparationStatus(tasks, { started: startedTasks, completed, reviewed: reviewedTasks });
    return {
      complete: state === "provided",
      status: locale === "es"
        ? { provided: "Información facilitada", in_progress: "En curso", not_started: "Sin iniciar" }[state]
        : { provided: "Information provided", in_progress: "In progress", not_started: "Not started" }[state],
    };
  }
  function openPreparationArea(key: string) {
    markTaskStarted(key === "profile_center" ? "profile_center" : key);
    if (key === "profile_center") { openProfessionalProfile(); return; }
    if (key === "passport") {
      setPassportErrors([]); setPassportStep(0); setPassportExpiringFlow(passportAvailability.reason === "expiring_soon"); setShowPassportAvailability(false);
    }
    if (key === "contact_details") setPersonalTab("contact");
    setFormErrors({}); setImmigrationHistoryErrors([]); setActiveTask(key);
  }
  const preparationCopy: Record<string, { title: string; description: string; kind: PreparationArea["kind"] }> = locale === "es" ? {
    passport: { title: "Centro de pasaportes", description: "Documento y datos del pasaporte", kind: "passport" },
    profile_center: { title: "Centro de perfil", description: "Experiencia y documentos de respaldo", kind: "profile" },
    contact_details: { title: "Información personal", description: "Contacto, domicilio y familiares", kind: "personal" },
    engagement: { title: "Datos del trabajo", description: "Su puesto, remuneración y fechas en esta producción", kind: "production" },
    immigration_history: { title: "Información migratoria", description: "Residencia, visas e historial migratorio", kind: "immigration" },
    legal_history: { title: "Antecedentes legales", description: "Arrestos, cargos y condenas", kind: "legal" },
  } : {
    passport: { title: "Passport Center", description: "Your passport and its details", kind: "passport" },
    profile_center: { title: "Profile Center", description: "Work history and supporting documents", kind: "profile" },
    contact_details: { title: "Personal information", description: "Contact, address, and family", kind: "personal" },
    engagement: { title: "Work details", description: "Your role, pay, and dates for this production", kind: "production" },
    immigration_history: { title: "Immigration information", description: "Residence, visas, and immigration history", kind: "immigration" },
    legal_history: { title: "Legal history", description: "Arrests, charges, and convictions", kind: "legal" },
  };
  const preparationAreas: PreparationArea[] = preparationTaskKeys({ personal: personalTasks, travel: travelDocumentTasks, production: experience.navigation.production, immigration: immigrationTasks }).map((key) => {
    const content = preparationCopy[key] ?? { title: taskTitle(key), description: "", kind: "documents" as const };
    let progress = areaStatus(key === "contact_details" && includesFamily ? [key, "accompanying_family"] : [key]);
    if (key === "passport" && !passportFileName) progress = { complete: false, status: locale === "es" ? "Falta la copia escaneada" : "Scan needed" };
    if (key === "passport" && passportFileName && !passportVerifiedPreview) progress = { complete: false, status: locale === "es" ? "Pendiente de revisión" : "Review needed" };
    return { key, ...content, ...progress };
  });

  const lastPassportFile = useRef<File | null>(null);
  /** Uploads the passport PDF, runs the server-side scan, and opens the extraction review. */
  async function scanPassportFile(file: File) {
    lastPassportFile.current = file;
    passportDemoAttempt.current += 1;
    setPassportScanning(true);
    setShowPassportAvailability(false);
    setActiveTask("passport");
    setPassportUploadResult({ state: "scanning", fields: {}, fileName: file.name });
    try {
      const res = await mvpApi.scanUserProfilePassport(file);
      const data = res?.data || {};
      if (data.storage_key) setPassportStorageKey(data.storage_key);
      const scanned = answersFromPassportScan(data.extracted, locale);
      const fields = Object.fromEntries(Object.entries(scanned).map(([id, value]) => [id, scalar(value)]).filter(([, value]) => value));
      const uploaded = Boolean(data.storage_key);
      if (uploaded) setPassportFileName(file.name);
      setPassportUploadResult({
        state: data.scan_error || !Object.keys(fields).length ? "failed" : "review",
        fields,
        fileName: file.name,
        uploaded,
      });
    } catch {
      setPassportUploadResult(null);
      setNotice({
        kind: "info",
        message: language === "ES" ? "No se pudo subir el pasaporte." : "The passport could not be uploaded.",
      });
    } finally {
      setPassportScanning(false);
    }
  }
  function retryPassportScan() {
    if (lastPassportFile.current) void scanPassportFile(lastPassportFile.current);
    else passportInputRef.current?.click();
  }
  function acceptPassportPreview(values: Record<string, unknown>) {
    setAnswers(values as Answers);
    setPassportFileName(passportUploadResult!.fileName);
    setPassportVerifiedPreview(true);
    void persistPassportStatus({...emptyPassportAvailability, reason:"received_pending_review"});
    setPassportExceptionDrafts({});
    setPassportUploadResult(null);
    setPassportStep(1);
    setReviewedTasks(current => new Set([...current, "passport"]));
    setEditingReviewGroup(false);
  }
  /** Scan saved the PDF but did not extract fields — keep the file and open the details form. */
  function continuePassportWithoutScan() {
    setPassportFileName(passportUploadResult!.fileName);
    setPassportVerifiedPreview(false);
    void persistPassportStatus({...emptyPassportAvailability, reason:"received_pending_review"});
    setPassportExceptionDrafts({});
    setPassportUploadResult(null);
    setPassportStep(1);
    setEditingReviewGroup(false);
  }

  function handlePassportFile(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    selectPassportFiles(files);
  }

  function selectPassportFiles(files: File[]) {
    if (!files.length || passportScanning) return;
    const file = singlePassportPdf(files);
    if (!file) {
      setNotice({
        kind: "info",
        message: language === "ES" ? "Seleccione un único archivo PDF." : "Choose one PDF file.",
      });
      return;
    }
    // File selection is not passport verification; the scan result is reviewed before it is accepted.
    if (openPassportIssue) setPassportAvailability(current => ({...current, reason: "received_pending_review"}));
    void scanPassportFile(file);
  }

  async function handleDocumentFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    const request = pendingDocument;
    event.target.value = "";
    if (!file || !request || request.id.endsWith(".passport")) return;
    setNotice({
      kind: "info",
      message: language === "ES" ? "Subiendo el documento…" : "Uploading the document…",
    });
    try {
      const res = await mvpApi.saveUserProfileDocument(request.id, file);
      const saved = (res?.data?.documents || []).find((row: { document_id?: string; file_name?: string }) => row.document_id === request.id);
      setDocumentFiles((current) => ({ ...current, [request.id]: saved?.file_name || file.name }));
      setNotice({
        kind: "success",
        message: language === "ES"
          ? `${file.name} se subió para ${localized(request.title, locale)}.`
          : `${file.name} was uploaded for ${localized(request.title, locale)}.`,
      });
    } catch {
      setNotice({
        kind: "info",
        message: language === "ES" ? "No se pudo subir el documento." : "The document could not be uploaded.",
      });
    }
  }

  /** Saves the travel-documents repeater (including an in-progress draft) and returns to its origin screen. */
  async function finishTravelDocuments() {
    const saved = entries(answers["pf.passport.other_details"]);
    const draft = (formDrafts?.drafts["pf.passport.other_details-draft"] ?? {}) as Entry;
    const editingIndex = formDrafts?.drafts["pf.passport.other_details-edit"];
    const started = Object.values(draft).some((value) => String(value ?? "").trim());
    let items = saved;
    if (started) {
      const required = ["document_kind", "number", "issuing_country", "expiry_date"];
      if (required.some((id) => !String(draft[id] ?? "").trim())) {
        setFormErrors({ "pf.passport.other_details": locale === "es" ? "Complete este documento antes de guardar." : "Finish this document before saving." });
        return;
      }
      const entry = Object.fromEntries(Object.entries(draft).map(([key, value]) => [key, String(value ?? "")]));
      const index = typeof editingIndex === "number" ? editingIndex : null;
      items = index === null ? [...saved, entry] : saved.map((item, itemIndex) => itemIndex === index ? entry : item);
      setAnswer("pf.passport.other_details", items);
      formDrafts?.setDrafts((all) => ({
        ...all,
        "pf.passport.other_details-draft": Object.fromEntries(Object.keys((all["pf.passport.other_details-draft"] ?? {}) as Entry).map((key) => [key, ""])),
        "pf.passport.other_details-edit": null,
      }));
    }
    setFormErrors({});
    setSavingProfile("other_travel_documents");
    try {
      await mvpApi.saveUserProfileTravelDocuments(travelDocumentsPayload({ ...answersRef.current, "pf.passport.other_details": items }));
    } catch {
      setNotice({ kind: "info", message: saveFailureMessage("other_travel_documents") });
      return;
    } finally {
      setSavingProfile(null);
    }
    setActiveTask(travelDocumentOrigin);
  }

  if (invitationStage === "active" || invitationStage === "inactive" || invitationStage === "signin") {
    return <InvitationOnboarding
      mode={invitationStage === "active" ? "invite" : invitationStage}
      locale={locale}
      onLocaleChange={(nextLocale) => { setLanguage(nextLocale === "es" ? "ES" : "EN"); setAnswer("pf.contact.language", nextLocale); }}
      onComplete={(contact) => {
        setAnswers((current) => ({...current,
          ...(!editedDefaultFields.current.has("pf.identity.given_names") && !supplied["pf.identity.given_names"] ? {"pf.identity.given_names": contact.givenNames} : {}),
          ...(!editedDefaultFields.current.has("pf.identity.family_primary") && !supplied["pf.identity.family_primary"] ? {"pf.identity.family_primary": contact.familyName} : {}),
          "pf.contact.email": contact.email, "pf.contact.phone": contact.phone, "pf.contact.language": locale}));
        window.history.replaceState({}, "", window.location.pathname);
        setInvitationStage(invitationStage === "signin" ? null : "accepted");
      }}
    />;
  }

  return (
    <>
    {workspaceIntroduction && <ParticipantWorkspaceIntroduction locale={locale} hideNextTime={hideWorkspaceIntroduction} onHideNextTimeChange={setHideWorkspaceIntroduction} onContinue={closeWorkspaceIntroduction} returning={workspaceIntroductionSeen} />}
    <main className="app-shell participant-home" hidden={activeTask === "profile_center" || workspaceIntroduction}>
      <header className="topbar">
        <a className="brand-wordmark" href="#top" aria-label="Participant workspace home">Global Mobility</a>

        <div className="top-actions">
          <span id="atlas-home-anchor" />
          <div className="language-control">
            <label className="sr-only" htmlFor="language">
              Language
            </label>
            <Globe2 size={16} aria-hidden="true" />
            <select
              id="language"
              value={language}
              onChange={(event) => { setLanguage(event.target.value); setAnswer("pf.contact.language", event.target.value === "ES" ? "es" : "en"); }}
              aria-label="Choose language"
            >
              <option value="EN">EN</option>
              <option value="ES">ES</option>
            </select>
            <ChevronDown size={14} aria-hidden="true" />
          </div>
          <div className="profile-wrap" ref={profileWrapRef}>
            <button
              ref={profileButtonRef}
              className="avatar-button"
              aria-label="Open profile menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {participantInitials}
            </button>
            {menuOpen && (
              <div className="profile-menu">
                <div>
                  <strong>{participantDisplayName}</strong>
                  <span>{participantEmail}</span>
                </div>
                <button onClick={() => { setMenuOpen(false); openProfessionalProfile(); }}>
                  {copy.editProfile}
                </button>
                {DEMO_PASSPORT_SWITCHER && <><label className="demo-fill-toggle"><input type="checkbox" checked={demoFillEnabled} onChange={event => setDemoFillEnabled(event.target.checked)} /> Fill forms with sample data</label><p className="demo-fill-note">Testing only. Your edits are kept. Uploads and verification remain manual.</p><DemoRouteControls inputs={demoContext} onChange={onDemoChange} /></>}
                <button onClick={() => setMenuOpen(false)}>{copy.closeMenu}</button>
              </div>
            )}
          </div>
        </div>
      </header>

      {routingMessage && <div className="workspace" role="status"><p>{routingMessage}</p></div>}
      <div className="desktop-layout" id="top" hidden={Boolean(routingMessage)}>
        <section className="workspace preparation-workspace" aria-label="Participant workspace">
          <input
            ref={passportInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handlePassportFile}
            className="sr-only"
            aria-label={language === "ES" ? "Subir PDF del pasaporte" : "Upload passport PDF"}
          />
          <input
            ref={documentInputRef}
            type="file"
            accept="application/pdf,image/*,.pdf,.heic,.heif"
            onChange={handleDocumentFile}
            className="sr-only"
            aria-label={localized(pendingDocument?.title, locale) || (language === "ES" ? "Añadir documento" : "Add document")}
          />

          <section className="welcome-section">
            <div className="welcome-row">
              <div>
                  {invitationStage === "accepted" && <span className="production-label">{invitationFlow.sample.production_name}</span>}
                  <h1>{invitationStage === "accepted" && participantGivenName
                    ? (locale === "es" ? `Bienvenido, ${participantGivenName}.` : `Welcome, ${participantGivenName}.`)
                    : copy.greeting}</h1>
                  <button type="button" className="workspace-overview-trigger" ref={workspaceOverviewTrigger} onClick={() => {
                    try { setHideWorkspaceIntroduction(localStorage.getItem(workspaceIntroductionKey) === "true"); } catch { /* Preference is optional. */ }
                    setWorkspaceIntroduction(true);
                  }}><CircleHelp size={16} aria-hidden="true"/>{locale === "es" ? "Cómo funciona" : "How this works"}</button>
              </div>
            </div>
          </section>

          <ParticipantPreparation areas={preparationAreas} onOpen={openPreparationArea} profileRef={professionalEntry} locale={locale} />
          <div className="preparation-extra">
                  {hasDocumentCenterRequests && <button className="document-center-card" onClick={() => setActiveTask("documents")}>
                    <span className="document-stack" aria-hidden="true"><i /><i /><i /></span>
                    <span className="document-center-copy">
                      <strong>{copy.documentCenter}</strong>
                    </span>
                    {documentCenterComplete
                      ? <span className="task-complete" aria-label={language === "ES" ? "Completado" : "Complete"}><Check size={15} strokeWidth={3} /></span>
                      : <span className="document-center-arrow" aria-hidden="true">&gt;</span>}
                  </button>}
          </div>
          <section className="trip-card" aria-labelledby="trip-heading">
            <div className="trip-card-top">
              <h2 id="trip-heading">{locale === "es" ? "Itinerario de vuelo" : "Flight itinerary"}</h2>
            </div>
            {experience.destination !== "US" ? <p>{locale === "es" ? "No se han añadido los datos del vuelo." : "Flight details have not been added."}</p> : <div className="route">
              <div className="route-point">
                <span>MAD</span>
                <small>Madrid</small>
              </div>
              <div className="route-line" aria-hidden="true">
                <span />
                <Plane size={18} />
                <span />
              </div>
              <div className="route-point end">
                <span>JFK</span>
                <small>{locale === "es" ? "Nueva York" : "New York"}</small>
              </div>
            </div>}
            {experience.destination === "US" && <div className="trip-meta">
              <span>{locale === "es" ? "14–26 oct 2026" : "Oct 14–26, 2026"}</span>
            </div>}
          </section>
        </section>
      </div>

      <ParticipantFooter locale={locale} />
      <AtlasHelp locale={locale} inSection={Boolean(activeTask)} />
      {activeTask && activeTask !== "profile_center" && (
        <section className={`task-screen${activeTask === "immigration_history" ? " immigration-flow-screen" : ""}${activeTask === "engagement" ? " engagement-flow-screen" : ""}${activeTask === "passport" ? " passport-flow-screen" : ""}${activeTask === "passport" && passportStep === 0 && !passportUploadResult && !showingReview && !showPassportAvailability ? " passport-upload-screen" : ""}`} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="task-screen-title">
          <header className="task-screen-header">
            <span id="atlas-section-anchor" />
            <button
              type="button"
              className="section-back-button"
              aria-label={backAccessibleLabel}
              onClick={() => passportUploadResult ? setPassportUploadResult(null) : activeTask === "passport" && showPassportAvailability ? (passportAvailabilityStep > 0 ? setPassportAvailabilityStep(0) : setShowPassportAvailability(false)) : activeTask === "passport" && showingReview ? (setPassportStep(0), setEditingReviewGroup(false)) : activeTask === "passport" && passportStep === 0 ? setActiveTask(null) : showingReview ? setActiveTask(null) : editingReviewGroup ? setEditingReviewGroup(false) : activeTask === "contact_details" && personalStep > 0 ? setPersonalTab(personalGroups[personalStep - 1].key) : activeTask === "engagement" && workStep > 0 ? setWorkStep((step) => step - 1) : activeTask === "professional_profile" && professionalProfileStep > 0
                  ? setProfessionalProfileStep((step) => step - 1)
                  : activeTask === "other_travel_documents" ? setActiveTask(travelDocumentOrigin)
                  : activeTask === "online_profiles" ? requestSocialExit("close")
                  : activeTask === "profile_assistant" && profileAssistantStep !== 5
                    ? previousProfileStep()
                  : activeTask === "passport" && passportStep > 0
                    ? setPassportStep((step) => step - 1)
                    : activeTask === "immigration_history" && immigrationHistoryStep > 0
                      ? setImmigrationHistoryStep((step) => step - 1)
                  : setActiveTask(null)}
            >
              <ArrowLeft size={20} />
              <span>{backLabel}</span>
            </button>
            {backDestination !== "home" ? <button className="task-home-link" type="button" aria-label={locale === "es" ? "Inicio del participante" : "Participant home"} onClick={() => setActiveTask(null)}><House size={17} aria-hidden="true"/><span>{locale === "es" ? "Inicio" : "Home"}</span></button> : <span className="task-brand">Global Mobility</span>}

          </header>
          {formProgress && <div className="task-progress" role="progressbar" aria-label={locale === "es" ? "Progreso de la sección" : "Section progress"} aria-valuemin={0} aria-valuemax={formProgress.total} aria-valuenow={formProgress.current}><span style={{width: `${formProgress.current / formProgress.total * 100}%`}} /></div>}
          <div className="task-screen-body"><div className="task-title-panel">
            {formProgress && <p className="guided-step-label">{locale === "es" ? "Paso" : "Step"} {formProgress.current} {locale === "es" ? "de" : "of"} {formProgress.total}</p>}
            <h2 id="task-screen-title">{activeTask === "passport" && showPassportAvailability ? (passportAvailabilityStep === 1 ? (["expired", "expiring_soon"].includes(passportRequestReason) ? (locale === "es" ? "Datos de renovación del pasaporte" : "Passport renewal details") : passportRequestReason === "lost_stolen" ? (locale === "es" ? "Datos de reemplazo del pasaporte" : "Passport replacement details") : (locale === "es" ? "Datos de solicitud del pasaporte" : "Passport request details")) : passportExpiringFlow ? (locale === "es" ? "Renovación del pasaporte" : "Passport renewal") : (locale === "es" ? "Estado del pasaporte" : "Passport status")) : activeTask === "passport" && passportStep === 0 && !passportUploadResult && !showingReview && !showPassportAvailability ? (locale === "es" ? "Centro de pasaportes" : "Passport Center") : activeTask === "passport" && showingReview ? (locale === "es" ? "Revisión del pasaporte" : "Passport review") : activeTask === "passport" && passportStep > 0 && !passportUploadResult ? (locale === "es" ? "Datos del pasaporte" : "Passport details") : showingReview ? `${locale === "es" ? "Revisión: " : "Review: "}${activeTaskTitle}` : activeTaskTitle}</h2></div>
            {!showingReview && activeTask === "passport" && passportStep === 0 && !passportFileName && !showPassportAvailability && !passportUploadResult && <div className="passport-later">
              <p>{locale === "es" ? "Puede añadir la copia escaneada más adelante y completar otras secciones mientras tanto." : "You can add your scan later and work on other sections in the meantime."}</p>
              <button type="button" onClick={() => setActiveTask(null)}>{locale === "es" ? "Trabajar en otra sección" : "Work on another section"}</button>
            </div>}
            {activeTask === "passport" && passportStep > 0 && !showingReview && !showPassportAvailability && !passportUploadResult && <div className="questionnaire-step-heading">
              {reviewGroups[passportStep]?.title !== "Passport details" && reviewGroups[passportStep]?.title !== "Datos del pasaporte" && <h3 className="history-step-title">{reviewGroups[passportStep]?.title}</h3>}
              {passportStep === 1 && !passportFileName && <p className="passport-file-requirement">{locale === "es" ? "Deberá subir una copia escaneada de su pasaporte para completar esta sección." : "You’ll need to upload a scanned copy of your passport to complete this section."}</p>}
            </div>}
            {!showingReview && activeTask === "professional_profile" && <div className="questionnaire-step-heading"><h3 className="history-step-title">{reviewGroups[professionalProfileStep]?.title}</h3></div>}
            {!showingReview && activeTask === "professional_profile" && <button className="profile-source-action text-action" type="button" onClick={() => { setReturnToProfessionalProfile(true); setProfileAssistantStep(5); setActiveTask("profile_assistant"); }}><Link2 size={17} aria-hidden="true" />{locale === "es" ? "Enlaces, currículum y notas" : "Create from resume, text or X"}<ChevronRight size={16} aria-hidden="true" /></button>}
            {!showingReview && activeTask === "engagement" && <div className="questionnaire-step-heading">
              <h3 className="history-step-title">{reviewGroups[workStep]?.title}</h3>
            </div>}
            {!showingReview && activeTask === "immigration_history" && <div className="questionnaire-step-heading">
              <h3 className="history-step-title">{immigrationHistoryGroups[immigrationHistoryStep]?.title}</h3>
              {immigrationHistoryGroups[immigrationHistoryStep]?.ids.some((id) => ["pf.identity.birth_city", "pf.identity.birth_country"].includes(id)) && <p className="collection-help">{locale === "es" ? "Indique los siguientes datos de nacimiento para sus formularios de EE. UU., aunque no estén impresos en su pasaporte." : "Provide the following birth details for your U.S. forms, even if they are not printed on your passport."}</p>}
            </div>}
            <form className="question-list" onSubmit={(event) => event.preventDefault()}>
              {activeTask === "passport" && passportUploadResult ? <PassportUploadReview key={passportUploadResult.fileName + passportDemoAttempt.current} state={passportUploadResult.state} fileName={passportUploadResult.fileName} fields={passportUploadResult.fields} existing={answers} labels={Object.fromEntries(passportReviewConcepts.map(c => [c.id, conciseLabels[c.id]?.[locale] ?? localized(c.label || c.prompt, locale)]))} es={locale === "es"} uploaded={passportUploadResult.uploaded} onRetry={retryPassportScan} onReplace={() => passportInputRef.current?.click()} onAccept={acceptPassportPreview} onContinue={continuePassportWithoutScan} /> : showingReview ? (
                <div className="questionnaire-review">
                  {reviewGroups.map((group, index) => <section className={`questionnaire-review-group${activeTask === "passport" && index === 0 ? " passport-pdf-review" : ""}`} key={group.title}>
                    {activeTask === "passport" && index === 0 ? <PassportCopyUpload fileName={passportFileName} awaitingVerification={passportVerifiedPreview} es={locale === "es"} onChoose={() => passportInputRef.current?.click()} onFiles={selectPassportFiles} /> : <>
                    <div className="questionnaire-review-heading"><h3>{reviewGroups.length === 1 ? (locale === "es" ? "Revisar" : "Review") : group.title}</h3>
                      {!(activeTask === "passport" && index === 0) && <button className="text-action" type="button" aria-label={`${locale === "es" ? "Editar" : "Edit"} ${group.title}`} onClick={() => {
                        setFormErrors({}); setPassportErrors([]); setImmigrationHistoryErrors([]);
                        if (activeTask === "passport") setPassportStep(index);
                        else if (activeTask === "engagement") setWorkStep(index);
                        else if (activeTask === "contact_details") setPersonalTab(personalGroups[index].key);
                        else if (activeTask === "professional_profile") setProfessionalProfileStep(index);
                        else setImmigrationHistoryStep(index);
                        setEditingReviewGroup(true);
                      }}>{locale === "es" ? "Editar" : "Edit"}</button>}
                    </div>
                    <dl>
                      {group.ids.map(id => allConcepts.find(c => c.id === id)).filter((c): c is ContractConcept => Boolean(c) && applicability(c!, answers, allConcepts) !== false).map(concept => <div key={concept.id}>
                        <dt>{conciseLabels[concept.id]?.[locale] ?? localized(concept.label || concept.prompt, locale)}</dt>
                        <dd>{reviewAnswer(concept)}</dd>
                      </div>)}
                      {activeTask === "immigration_history" && group.ids.includes("pf.location.country_of_legal_residence") && scalar(answers["pf.location.expected_visa_application_country"]) && <div><dt>{locale === "es" ? "País donde solicitará su visa" : "Country where you will apply for your visa"}</dt><dd>{countryDisplayName(scalar(answers["pf.location.expected_visa_application_country"]), locale)}</dd></div>}
                    </dl></>}
                  </section>)}
                </div>
              ) : activeTask === "passport" ? (
                passportGroups.map((conceptIds, step) => (
                  <div className="passport-stage" hidden={passportStep !== step} key={`passport-${step}`}>
                    {step === 0 ? (showPassportAvailability ? <PassportAvailabilityForm key={passportExpiringFlow ? "expiring" : "missing"} onDraftChange={value => setPassportExceptionDrafts(current => ({...current, [passportExpiringFlow ? "expiring" : "missing"]: value}))} destination={experience.destination} onStatusChange={value => { void persistPassportStatus(value); }} requiredDate={typeof demoContext.first_location_date === "string" ? demoContext.first_location_date : undefined} step={passportAvailabilityStep} onStepChange={(step, reason) => { setPassportRequestReason(reason); setPassportAvailabilityStep(step); }} initial={passportExceptionDrafts[passportExpiringFlow ? "expiring" : "missing"] ?? (passportExpiringFlow ? (passportAvailability.reason === "expiring_soon" ? passportAvailability : {...emptyPassportAvailability, reason: "expiring_soon"}) : (["expiring_soon", "received_pending_review"].includes(passportAvailability.reason) ? emptyPassportAvailability : passportAvailability))} locale={locale} onAlternative={experience.destination === "IT" ? () => openExceptionalTravelDocuments("passport") : undefined} onSave={value => { void persistPassportStatus(value); setShowPassportAvailability(false); setActiveTask(null); }} /> :
                      <PassportActions
                        reviewed={reviewedTasks.has("passport")}
                        locale={locale}
                        onContinue={continuePassport}
                        onMissing={() => {setPassportExpiringFlow(false); setPassportAvailabilityStep(0); setShowPassportAvailability(true);}}
                        onExpiring={() => {setPassportExpiringFlow(true); setPassportAvailabilityStep(0); setShowPassportAvailability(true);}}
                        fileName={passportFileName}
                        currentCountry={scalar(answers["pf.location.current_country"]) || String(demoContext.passport_country ?? "")}
                        onUpload={() => passportInputRef.current?.click()}
                        onFiles={selectPassportFiles}
                        onRemove={() => {
                          setPassportFileName("");
                          setPassportVerifiedPreview(false);
                          if (passportInputRef.current) passportInputRef.current.value = "";
                        }}
                      />
                    ) : conceptIds.map((conceptId) => passportReviewConcepts.find((concept) => concept.id === conceptId)).filter((concept): concept is ContractConcept => Boolean(concept)).map((concept) => (
                      <ContractQuestion
                        key={concept.id}
                        concept={concept}
                        locale={locale}
                        answers={answers}
                        focused={concept.answer.shape === "country_reference"}
                        invalid={passportErrors.includes(concept.id)}
                        setAnswer={setAnswer}
                        error={formErrors[concept.id]}
                      />
                    ))}
                  </div>
                ))
              ) : activeTask === "documents" ? (
                <DocumentList
                  onOtherTravelDocuments={() => openExceptionalTravelDocuments("documents")}
                  documents={visibleDocuments}
                  requests={caseDocumentRequests}
                  responses={documentResponses}
                  savedDocuments={savedDocuments}
                  onReview={(task) => {if (task === "professional_profile") openProfessionalProfile(); else setActiveTask("accompanying_family");}}
                  onResponse={(document, reason) => {setSavedDocuments(current => ({...current, [document.id]: document})); setDocumentResponses(current => ({...current, [document.id]: reason})); setNotice({kind: "info", message: locale === "es" ? "Respuesta guardada. El documento sigue pendiente." : "Response saved. The document is still outstanding."});}}
                  answers={answers}
                  locale={locale}
                  files={documentFiles}
                  passportFileName={passportFileName}
                  onPassport={() => {
                    setPassportStep(0);
                    setActiveTask("passport");
                  }}
                  onChoose={(document) => {
                    setSavedDocuments(current => ({...current, [document.id]: document}));
                    setPendingDocument(document);
                    window.setTimeout(() => documentInputRef.current?.click(), 0);
                  }}
                />
              ) : activeTask === "profile_assistant" ? (
                <ProfessionalProfileAssistant
                  locale={locale}
                  step={profileAssistantStep}
                  sourceText={profileSourceText}
                  sourceFiles={profileSourceFiles}
                  uploadingFiles={uploadingResumes}
                  fetchingXAccount={fetchingXAccount}
                  generating={generatingProfile}
                  draft={profileDraft}
                  profiles={socialProfiles}
                  selectedProfileSources={selectedProfileSources}
                  onDiscard={discardProfessionalProfileDraft}
                  onStepChange={changeProfileAssistantStep}
                  onSourceTextChange={setProfileSourceText}
                  onUploadFiles={files => { void uploadResumeFiles(files); }}
                  onRemoveFile={file => { void removeResumeFile(file); }}
                  onUseXAccount={handle => { void useXAccountForProfile(handle); }}
                  onDraftChange={setProfileDraft}
                  onProfilesChange={updateSocialProfiles}
                  onSelectedProfileSourcesChange={setSelectedProfileSources}
                  onRegenerate={() => { void generateProfessionalProfileDraft(); }}
                />
              ) : activeTask === "professional_profile" ? professionalProfileGroups.map((conceptIds, step) => (
                <div className="professional-profile-stage" hidden={professionalProfileStep !== step} key={conceptIds.join("-")}>
                  {conceptIds.map((conceptId) => activeConcepts.find((concept) => concept.id === conceptId)).filter((concept): concept is ContractConcept => Boolean(concept)).map((concept) => {
                    const field = <ContractQuestion
                      hideLabel={step === 2}
                      key={concept.id}
                      concept={concept}
                      locale={locale}
                      answers={answers}
                      setAnswer={setAnswer}
                        error={formErrors[concept.id]}
                      onOpenProfileAssistant={["pf.career.biography", "pf.career.credits"].includes(concept.id) ? () => {
                        setReturnToProfessionalProfile(true);
                        setProfileAssistantStep(5);
                        setActiveTask("profile_assistant");
                      } : undefined}
                    />;
                    return step === 2 ? <details className="evidence-group" key={concept.id} open={formErrors[concept.id] ? true : undefined}><summary>{localized(concept.label, locale)}<ChevronDown size={18} /></summary><div>{field}</div></details> : field;
                  })}
                </div>
              )) : activeTask === "immigration_history" ? immigrationHistoryGroups.map((group, step) => (
                <div className="immigration-history-stage" hidden={immigrationHistoryStep !== step} key={group.title}>
                  {group.ids.map((conceptId) => activeConcepts.find((concept) => concept.id === conceptId)).filter((concept): concept is ContractConcept => Boolean(concept)).map((concept) => (
                    <ContractQuestion
                      key={concept.id}
                      concept={concept}
                      locale={locale}
                      answers={answers}
                      focused={concept.answer.shape === "country_reference"}
                      invalid={immigrationHistoryErrors.includes(concept.id)}
                      saveSignal={otherNamesSaveSignal}
                      setAnswer={setAnswer}
                        error={formErrors[concept.id]}
                    />
                  ))}
                  {group.ids.includes("pf.location.country_of_legal_residence") && scalar(answers["pf.location.country_of_legal_residence"]) && <fieldset className="question-field visa-location-question">
                    <legend>{locale === "es" ? `¿Tiene previsto solicitar su visa fuera de ${countryDisplayName(scalar(answers["pf.location.country_of_legal_residence"]), locale)}? (opcional)` : `Do you plan to apply for your visa outside ${countryDisplayName(scalar(answers["pf.location.country_of_legal_residence"]), locale)}? (optional)`}</legend>
                    <div className="choice-row">{["yes", "no"].map(value => <label key={value}><input type="radio" name="apply-outside-residence" checked={scalar(answers["pf.location.apply_outside_residence"]) === value} onChange={() => setAnswer("pf.location.apply_outside_residence", value)} />{value === "yes" ? (locale === "es" ? "Sí" : "Yes") : "No"}</label>)}</div>
                    {scalar(answers["pf.location.apply_outside_residence"]) === "yes" && <div className="visa-country-followup">
                      <label htmlFor="visa-application-country">{locale === "es" ? "País donde solicitará su visa" : "Country where you will apply for your visa"}</label>
                      <CountryPicker id="visa-application-country" label={locale === "es" ? "País donde solicitará su visa" : "Country where you will apply for your visa"} describedBy={formErrors["pf.location.expected_visa_application_country"] ? "visa-country-error" : undefined} invalid={Boolean(formErrors["pf.location.expected_visa_application_country"])} locale={locale} multiple={false} value={scalar(answers["pf.location.expected_visa_application_country"]) ? [scalar(answers["pf.location.expected_visa_application_country"])] : []} onChange={codes => setAnswer("pf.location.expected_visa_application_country", codes[0] ?? "")} />
                      {formErrors["pf.location.expected_visa_application_country"] && <p id="visa-country-error" className="field-error" role="alert">{formErrors["pf.location.expected_visa_application_country"]}</p>}
                    </div>}
                  </fieldset> }
                </div>
              )) : activeTask === "legal_history" ? (
                <div className="legal-history-stage">
                  {legalHistoryIds.map((conceptId, index) => {
                    const previousAnswered = index === 0 || Boolean(scalar(answers[legalHistoryIds[index - 1]]));
                    const concept = activeConcepts.find((candidate) => candidate.id === conceptId);
                    return previousAnswered && concept ? (
                    <ContractQuestion
                      key={concept.id}
                      concept={concept}
                      locale={locale}
                      answers={answers}
                      setAnswer={setAnswer}
                        error={formErrors[concept.id]}
                    />
                    ) : null;
                  })}
                </div>
              ) : activeTask === "contact_details" ? <div className="personal-details-step">
                <h3 className="history-step-title">{personalGroups[personalStep].title}</h3>
                <div className="personal-step-fields">{activeConcepts.filter(c => personalGroups[personalStep].ids.includes(c.id)).map(concept => concept.id === "pf.identity.other_names"
                  ? <ContractQuestion key={concept.id} concept={concept} locale={locale} answers={answers} setAnswer={setAnswer} saveSignal={otherNamesSaveSignal} error={formErrors[concept.id]} />
                  : <ContactDetail key={concept.id} concept={concept} locale={locale} answers={answers} setAnswer={setAnswer} error={formErrors[concept.id]} />)}</div>
              </div>
              : (activeTask === "engagement" ? workGroups[workStep].map((id) => activeConcepts.find((concept) => concept.id === id)).filter((concept): concept is ContractConcept => Boolean(concept)) : activeConcepts).map((concept) => (
                <ContractQuestion
                  key={concept.id}
                  concept={concept}
                  locale={locale}
                  answers={answers}
                  setAnswer={setAnswer}
                        error={formErrors[concept.id]}
                  socialProfiles={concept.id === "pf.social.accounts" ? socialProfiles : undefined}
                  onSocialProfilesChange={concept.id === "pf.social.accounts" ? updateSocialProfiles : undefined}
                />
              ))}
              {activeTask !== "engagement" && activeTask !== "contact_details" && activeTask !== "professional_profile" && activeTask !== "profile_assistant" && activeTask !== "online_profiles" && activeTask !== "passport" && activeTask !== "accompanying_family" && activeTask !== "immigration_history" && activeTask !== "legal_history" && <button className={`return-button ${activeTask === "documents" ? "document-center-return" : ""}`} type="button" disabled={savingProfile === "other_travel_documents"} onClick={() => activeTask === "other_travel_documents" ? void finishTravelDocuments() : setActiveTask(null)}>
                {activeTask === "other_travel_documents"
                  ? savingProfile === "other_travel_documents" ? (language === "ES" ? "Guardando…" : "Saving…") : (language === "ES" ? "Listo" : "Done")
                  : (language === "ES" ? "Volver" : "Return")}
              </button>}
            </form>
          </div>
          {showingReview && <footer className="task-screen-footer"><button type="button" onClick={() => setActiveTask(null)}>{locale === "es" ? "Cerrar" : "Close"}</button></footer>}
          {!showingReview && activeTask === "engagement" && <footer className="task-screen-footer">{!editingReviewGroup && workStep > 0 && <button className="step-previous" type="button" onClick={() => setWorkStep(step => step - 1)}><ArrowLeft size={18} aria-hidden="true" />{locale === "es" ? "Anterior" : "Previous"}</button>}<button type="button" disabled={savingProfile === "engagement"} onClick={continueWork}>{primaryLabel("engagement", editingReviewGroup ? (locale === "es" ? "Guardar cambios" : "Save changes") : workStep < workGroups.length - 1 ? (locale === "es" ? "Continuar" : "Continue") : (locale === "es" ? "Revisar respuestas" : "Review answers"))}</button></footer>}
          {!showingReview && activeTask === "contact_details" && <footer className="task-screen-footer">{personalStep > 0 && <button className="step-previous" type="button" onClick={() => setPersonalTab(personalGroups[personalStep - 1].key)}><ArrowLeft size={18} aria-hidden="true" />{locale === "es" ? "Anterior" : "Previous"}</button>}<button type="button" disabled={savingProfile === "contact_details"} onClick={finishContact}>{primaryLabel("contact_details", personalStep < personalGroups.length - 1 ? (locale === "es" ? "Continuar" : "Continue") : (locale === "es" ? "Listo" : "Done"))}</button></footer>}
          {!showingReview && activeTask === "professional_profile" && <footer className="task-screen-footer">{!editingReviewGroup && professionalProfileStep > 0 && <button className="step-previous" type="button" onClick={() => setProfessionalProfileStep(step => step - 1)}><ArrowLeft size={18} aria-hidden="true" />{locale === "es" ? "Anterior" : "Previous"}</button>}<button type="button" disabled={savingProfile === "professional_profile"} onClick={continueProfessionalProfile}>{primaryLabel("professional_profile", editingReviewGroup ? (locale === "es" ? "Guardar cambios" : "Save changes") : professionalProfileStep < professionalProfileGroups.length - 1 ? (locale === "es" ? "Continuar" : "Continue") : (locale === "es" ? "Revisar respuestas" : "Review answers"))}</button></footer>}
          {activeTask === "profile_assistant" && profileAssistantStep !== 5 && <footer className={`task-screen-footer${profileAssistantStep === 2 ? " x-step-footer" : ""}`}>{profileAssistantStep === 2 ? <button className="step-previous" type="button" onClick={previousProfileStep}><ArrowLeft size={18} aria-hidden="true" />{language === "ES" ? "Anterior" : "Previous"}</button> : <>{!hasProfessionalProfile && <button className="step-previous" type="button" onClick={previousProfileStep}><ArrowLeft size={18} aria-hidden="true" />{locale === "es" ? "Anterior" : "Previous"}</button>}<button type="button" disabled={generatingProfile || (profileAssistantStep === 4 ? !profileDraft.trim() : profileAssistantStep === 0 ? !profileSourceFiles.length : profileAssistantStep === 1 ? !profileSourceText.trim() : !selectedProfileSources.length)} onClick={profileAssistantStep === 4 ? saveProfessionalProfileDraft : () => { void generateProfessionalProfileDraft(); }}>{primaryLabel("professional_profile", generatingProfile ? (language === "ES" ? "Creando…" : "Creating…") : profileAssistantStep === 4 ? (language === "ES" ? "Guardar perfil" : "Save profile") : (language === "ES" ? "Generar perfil" : "Generate profile"))}</button></>}</footer>}
          {activeTask === "online_profiles" && <footer className="task-screen-footer"><button type="button" disabled={savingProfile === "online_profiles"} onClick={finishOnlineProfiles}>{primaryLabel("online_profiles", language === "ES" ? "Listo" : "Done")}</button></footer>}
          {!passportUploadResult && !showingReview && activeTask === "passport" && !(passportStep === 0 && showPassportAvailability) && (passportStep > 0 || Boolean(passportFileName)) && <footer className="task-screen-footer passport-footer">
            {!editingReviewGroup && passportStep > 0 && <button className="step-previous" type="button" onClick={() => setPassportStep(step => step - 1)}><ArrowLeft size={18} aria-hidden="true" />{locale === "es" ? "Anterior" : "Previous"}</button>}
            <button type="button" className={passportStep === 0 && !passportFileName ? "passport-manual-entry" : undefined} disabled={savingProfile === "passport" || passportScanning} onClick={continuePassport}>
              {primaryLabel("passport", editingReviewGroup ? (locale === "es" ? "Guardar cambios" : "Save changes") : passportStep === 0
                ? passportFileName
                  ? (language === "ES" ? "Continuar con los datos" : "Continue to passport details")
                  : (language === "ES" ? "Introducir datos sin subir el pasaporte" : "Enter details without uploading")
                : passportStep < passportGroups.length - 1
                  ? (language === "ES" ? "Continuar" : "Continue")
                  : (language === "ES" ? "Revisar respuestas" : "Review answers"))}
            </button>
          </footer>}
          {!showingReview && activeTask === "accompanying_family" && <footer className="task-screen-footer"><button type="button" disabled={savingProfile === "accompanying_family" || (answers["pf.family.accompanying"] !== "no" && !(answers["pf.family.accompanying"] === "yes" && familyMemberCount > 0 && !familyHasDraft))} onClick={finishAccompanyingFamily}>{primaryLabel("accompanying_family", editingReviewGroup ? (locale === "es" ? "Guardar cambios" : "Save changes") : (locale === "es" ? "Revisar respuestas" : "Review answers"))}</button></footer>}
          {!showingReview && activeTask === "immigration_history" && <footer className="task-screen-footer">{!editingReviewGroup && immigrationHistoryStep > 0 && <button className="step-previous" type="button" onClick={() => setImmigrationHistoryStep(step => step - 1)}><ArrowLeft size={18} aria-hidden="true" />{locale === "es" ? "Anterior" : "Previous"}</button>}<button type="button" disabled={savingProfile === "immigration_history"} onClick={continueImmigrationHistory}>{primaryLabel("immigration_history", editingReviewGroup ? (locale === "es" ? "Guardar cambios" : "Save changes") : immigrationHistoryStep < immigrationHistoryGroups.length - 1 ? (language === "ES" ? "Continuar" : "Continue") : (language === "ES" ? "Revisar respuestas" : "Review answers"))}</button></footer>}
          {!showingReview && activeTask === "legal_history" && <footer className="task-screen-footer"><button type="button" disabled={savingProfile === "legal_history" || !legalHistoryComplete} onClick={finishLegalHistory}>{primaryLabel("legal_history", editingReviewGroup ? (locale === "es" ? "Guardar cambios" : "Save changes") : (locale === "es" ? "Revisar respuestas" : "Review answers"))}</button></footer>}
        </section>
      )}

      <dialog ref={socialExitDialog} className="social-exit-dialog" aria-labelledby="social-exit-title" onCancel={(event) => { event.preventDefault(); setSocialExit(null); }}>
        <h2 id="social-exit-title">{locale === "es" ? "¿Guardar los cambios de esta cuenta?" : "Save changes to this account?"}</h2>
        <div className="social-exit-actions">
          <button type="button" onClick={saveSocialAndExit}>{locale === "es" ? "Guardar cambios" : "Save changes"}</button>
          <button type="button" className="secondary" onClick={() => completeSocialExit(socialExit ?? "close")}>{locale === "es" ? "Descartar cambios" : "Discard changes"}</button>
          <button type="button" className="secondary" autoFocus onClick={() => setSocialExit(null)}>{locale === "es" ? "Seguir editando" : "Keep editing"}</button>
        </div>
      </dialog>

      {notice && (
        <div className={`toast ${notice.kind}`} role="status">
          {notice.kind === "success" ? <Check size={18} /> : <Camera size={18} />}
          <span>{notice.message}</span>
          <button aria-label="Dismiss message" onClick={() => setNotice(null)}><X size={16} /></button>
        </div>
      )}
    </main>
    {professionalCenterMounted && <div className="profile-center-host" hidden={activeTask !== "profile_center"}>
      <ProfessionalProfileCenter
        onReturnHome={() => setActiveTask(null)}
        onWorkStarted={() => markTaskStarted("profile_center")}
        initialSummary={savedBiography}
        initialEntries={entries(answers["pf.career.credits"]).map((item, index) => ({
          id: `credit-${index}`,
          title: item.production_title || "",
          role: item.role || "",
          year: item.year || "",
          detail: item.notes || "",
          category: "Credits",
        }))}
        onUploadFiles={async (files) => {
          for (const file of files) {
            try { await mvpApi.uploadUserProfileResume(file); }
            catch { setNotice({ kind: "info", message: locale === "es" ? "No se pudo subir el archivo." : "The file could not be uploaded." }); }
          }
        }}
        onSaveNotes={async (notes) => { await persistExperienceText(notes.map((note) => note.text).join("\n\n")); }}
        onPrepareDraft={async (input) => {
          try {
            const { data } = await mvpApi.synthesizeProfessionalProfile({
              procedureSlug: account.procedureSlug,
              notes: input.notes,
              links: input.links,
              existingFileNames: input.fileNames,
            });
            const generated = answersFromSynthesizedProfile(data);
            const biography = scalar(generated["pf.career.biography"]).trim();
            setAnswers((current) => fillEmptyAnswers(current, generated));
            return {
              summary: biography,
              entries: entries(generated["pf.career.credits"]).map((item, index) => ({
                id: `credit-${index}`,
                title: item.production_title || "",
                role: item.role || "",
                year: item.year || "",
                detail: item.notes || "",
                category: "Credits",
              })),
            };
          } catch (error: any) {
            const detail = error?.response?.data?.detail;
            setNotice({
              kind: "info",
              message: typeof detail === "string" && detail
                ? detail
                : locale === "es" ? "No se pudo crear su perfil. Inténtelo de nuevo." : "Your profile could not be created. Please try again.",
            });
            return null;
          }
        }}
        onConfirmDraft={async ({ summary, entries: creditEntries }) => {
          delete insertedDemoAnswers.current["pf.career.biography"];
          const next = {
            ...answersRef.current,
            "pf.career.biography": summary,
            "pf.career.credits": creditEntries.map((entry) => ({
              production_title: entry.title,
              role: entry.role,
              production_type: "",
              production_type_other: "",
              year: entry.year,
              director_or_producer: "",
              source_url: "",
              notes: entry.detail,
            })),
          };
          setAnswers(next);
          try {
            await mvpApi.saveUserProfileProfessionalProfile(professionalProfilePayload(["pf.career.biography"], next));
            await mvpApi.saveUserProfileProfessionalProfile(professionalProfilePayload(["pf.career.credits"], next));
            setCompletedTasks((current) => new Set(current).add("profile_center"));
            setReviewedTasks((current) => new Set(current).add("profile_center"));
          } catch {
            setNotice({ kind: "info", message: saveFailureMessage("professional_profile") });
          }
        }}
        onVerifyAccount={async ({ platform, account, url }) => {
          try {
            const { data } = await mvpApi.verifyUserProfileOnlineAccount({ platform, account: account || url });
            const status = data?.status === "found" || data?.status === "restricted" || data?.status === "not_found" ? data.status : "unavailable";
            return { status, simulated: false };
          } catch (error: any) {
            const code = error?.response?.status;
            if (code === 400) throw error;
            if (code === 404) return { status: "not_found", simulated: false };
            return { status: "unavailable", simulated: false };
          }
        }}
      />
    </div>}
    </>
  );
}
